// DEPLOY: /var/www/woody.wtf/frontend/src/pages/StrategyBuilder.js
// DCA strategy builder/backtester. Mobile keeps the picker button in the navbar.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Chart, registerables } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin from 'chartjs-plugin-annotation';
import 'chartjs-adapter-date-fns';
import api from '../api';
import {
  uid, blankCond, condAgg, serialiseCond, deserialiseCond,
  UNARY_OPS, MARGIN_OPS,
  sSel, sMeta, sRemBtn, sIn, sDrop, sDI, sDSym, sDName,
  TickerSearch, ConditionRow,
} from '../components/ConditionEditor';
import SignalChart from '../components/SignalChart';

Chart.register(...registerables, zoomPlugin, annotationPlugin);

// ── Constants (StrategyBuilder-only) ────────────────────────────────────────────
const COLORS = ['#7C3AED','#059669','#DC2626','#D97706','#2563EB','#DB2777','#0891B2','#65A30D'];
const SIGNAL_COLORS = ['#16A34A','#DC2626','#2563EB','#D97706','#DB2777','#0891B2','#65A30D','#7C3AED'];
const signalColor = (sig,i) => /^#[0-9a-f]{6}$/i.test(sig?.color||'') ? sig.color : SIGNAL_COLORS[i%SIGNAL_COLORS.length];
const REBALANCE_UNITS = [{value:'days',label:'days'},{value:'weeks',label:'weeks'},{value:'months',label:'months'},{value:'years',label:'years'}];

const DAY_MS   = 864e5;
const MONTH_MS = 30.44 * DAY_MS;   // approx — fine for a sliding view window
const YEAR_MS  = 365.25 * DAY_MS;
function ymToMs(y,m)       { return y*YEAR_MS + m*MONTH_MS; }
function msToYM(ms)        { const months=Math.round(ms/MONTH_MS); return {y:Math.floor(months/12),m:months%12}; }

function levelColor(l)     { return l<=3?'rgba(22,163,74,0.1)':l<=6?'rgba(234,179,8,0.1)':'rgba(220,38,38,0.1)'; }
// Chart.js draws on canvas, so its colours are read from the theme variables at draw time.
const css=n=>getComputedStyle(document.documentElement).getPropertyValue(n).trim();
function levelBorder(l)    { return css(l<=3?'--success':l<=6?'--warning':'--error'); }
function levelTextClr(l)   { return l<=3?'#15803d':l<=6?'#92400e':'#991b1b'; }
function fmt$(v)           { return v==null?'-':'$'+Number(v).toLocaleString('en-AU',{minimumFractionDigits:2,maximumFractionDigits:2}); }
function fmtPct(v)         { return v==null?'-':(v>=0?'+':'')+Number(v).toFixed(2)+'%'; }

function blankSignal()     { return {id:uid(),label:'',color:SIGNAL_COLORS[0],target_regime_id:'',direction:'bullish',type:'conditions',smc:blankSmc(),conditions:[blankCond()]}; }
function blankSmc()        { return {ticker:'',trigger:'choch',dispAtr:1}; }
function deserialiseSmc(s) { return {ticker:s?.ticker||'',trigger:s?.trigger||'choch',dispAtr:s?.disp_atr??1}; }
function serialiseSmc(s)   { return {ticker:s.ticker,trigger:s.trigger,disp_atr:Number(s.dispAtr)||0}; }
function blankLevel(n)     { return {id:uid(),level:n,label:`Regime ${n}`,enabled:true,allocations:[{id:uid(),ticker:'CASH',pct:100}],dca_ticker:'CASH'}; }
function blankStrategy()   {
  const levels=[blankLevel(1),blankLevel(2),blankLevel(3)];
  return {backtest_timeframe:'1d',base_currency:'AUD',dca_amount:0,dca_frequency:{n:1,unit:'months'},rebalance_every:null,starting_regime_id:levels[levels.length-1].id,regime_levels:levels,signals:[]};
}

function useWindowWidth()  { const [w,setW]=useState(window.innerWidth); useEffect(()=>{const h=()=>setW(window.innerWidth);window.addEventListener('resize',h);return()=>window.removeEventListener('resize',h);},[]);return w; }

// Allocation entry serialise/deserialise. Fixed: {ticker, pct}. Rank-ref:
// {rank_ref:{list_id, rank}, pct}. UI carries kind + listId/rank for rank entries.
const serialiseAlloc = a => a.kind==='rank'
  ? {rank_ref:{list_id:Number(a.listId)||0,rank:Number(a.rank)||1},pct:Number(a.pct)||0}
  : {ticker:a.ticker,pct:Number(a.pct)||0};
const deserialiseAlloc = a => a.rank_ref
  ? {id:uid(),kind:'rank',ticker:'',listId:a.rank_ref.list_id,rank:a.rank_ref.rank,pct:a.pct}
  : {id:uid(),kind:'fixed',ticker:a.ticker,pct:a.pct};

// ── AllocationRow ─────────────────────────────────────────────────────────────
function AllocationRow({alloc,lists,onChange,onRemove,error=false}) {
  const kind=alloc.kind||'fixed';
  const isCash=kind==='fixed'&&alloc.ticker==='CASH';
  const mode=kind==='rank'?'rank':(isCash?'cash':'ticker');
  return (
    <div style={{display:'flex',gap:'6px',alignItems:'center',marginBottom:'6px',flexWrap:'wrap'}}>
      <select style={sSel} value={mode} onChange={e=>{
        const v=e.target.value;
        if(v==='cash')        onChange({kind:'fixed',ticker:'CASH'});
        else if(v==='ticker') onChange({kind:'fixed',ticker:''});
        else                  onChange({kind:'rank',ticker:'',listId:alloc.listId||(lists[0]&&lists[0].id)||'',rank:alloc.rank||1});
      }}>
        <option value="ticker">Ticker</option>
        <option value="cash">Cash</option>
        <option value="rank">List rank</option>
      </select>
      {mode==='rank'
        ? <>
            <select style={sSel} value={alloc.listId||''} onChange={e=>onChange({listId:parseInt(e.target.value)||''})}>
              <option value="">— list —</option>
              {lists.map(l=><option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <span style={sMeta}>rank #</span>
            <input style={sIn('44px')} type="number" min="1" value={alloc.rank||1} onChange={e=>onChange({rank:parseInt(e.target.value)||1})}/>
          </>
        : isCash
          ? <span style={{padding:'4px 10px',border:'1px solid var(--success)',borderRadius:'var(--radius)',fontSize:'12px',color:'var(--success)',fontFamily:"'JetBrains Mono',monospace",background:'var(--success-dim)'}}>💵 CASH</span>
          : <TickerSearch value={alloc.ticker} onChange={v=>onChange({ticker:v})} placeholder="Search…" width="140px" error={error}/>
      }
      <input style={sIn('56px')} type="number" min="0" max="100" value={alloc.pct} onChange={e=>onChange({pct:e.target.value})}/>
      <span style={sMeta}>%</span>
      <button style={sRemBtn} onClick={onRemove}>✕</button>
    </div>
  );
}

// ── DcaTargetRow ──────────────────────────────────────────────────────────────
function DcaTargetRow({value,onChange}) {
  return (
    <div style={{display:'flex',gap:'6px',alignItems:'center',flexWrap:'wrap'}}>
      <TickerSearch value={value==='CASH'?'':value} onChange={onChange} placeholder="Search…" width="140px"/>
      <button
        style={{padding:'4px 10px',fontSize:'11px',border:'1px solid var(--success)',borderRadius:'var(--radius)',background:value==='CASH'?'var(--success)':'transparent',color:value==='CASH'?'var(--on-accent)':'var(--success)',cursor:'pointer'}}
        onClick={()=>onChange('CASH')}
      >💵 Cash</button>
      {value&&value!=='CASH'&&<span style={{fontSize:'11px',color:'var(--text-muted)'}}>→ {value}</span>}
    </div>
  );
}

// ── LibDropdown ────────────────────────────────────────────────────────────────
// A "Load saved ▾" control listing library items. Click a name to load a copy;
// click ✕ to delete it from the library (with confirmation handled by onDelete).
function LibDropdown({label,items,open,onToggle,onPick,onDelete}) {
  const wrap=useRef(null);
  useEffect(()=>{const h=e=>{if(wrap.current&&!wrap.current.contains(e.target))open&&onToggle();};document.addEventListener('mousedown',h);return()=>document.removeEventListener('mousedown',h);},[open,onToggle]);
  return (
    <div ref={wrap} style={{position:'relative',display:'inline-block'}}>
      <button
        onClick={onToggle}
        style={{padding:'3px 10px',fontSize:'11px',border:'1px solid var(--accent)',borderRadius:'20px',background:'transparent',color:'var(--accent)',cursor:'pointer',fontFamily:"'JetBrains Mono',monospace",fontWeight:600}}
      >{label} ▾</button>
      {open&&(
        <div style={{position:'absolute',top:'100%',left:0,marginTop:'3px',width:'260px',maxHeight:'260px',overflowY:'auto',background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius)',zIndex:300,boxShadow:'0 4px 12px rgba(0,0,0,0.15)'}}>
          {items.length===0&&<div style={{padding:'8px 10px',fontSize:'11px',color:'var(--text-muted)'}}>Nothing saved yet.</div>}
          {items.map(it=>(
            <div key={it.id} style={{display:'flex',alignItems:'center',borderBottom:'1px solid var(--border)'}}>
              <button onClick={()=>onPick(it.id)} title={it.name} style={{flex:1,minWidth:0,textAlign:'left',padding:'7px 10px',background:'none',border:'none',cursor:'pointer',fontSize:'12px',color:'var(--text)',fontFamily:"'JetBrains Mono',monospace",overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{it.name}</button>
              <button onClick={()=>onDelete(it.id,it.name)} title="Delete from library" style={{flexShrink:0,padding:'4px 9px',background:'none',border:'none',cursor:'pointer',color:'var(--error)',fontSize:'12px'}}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function StrategyBuilder() {
  const {user} = useAuth();
  const navigate      = useNavigate();
  const isMobile      = useWindowWidth() < 768;

  const [strategies,  setStrategies]  = useState([]);
  const [lists,       setLists]       = useState([]);   // user's lists, for rank-ref allocations
  const [activeId,    setActiveId]    = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [showPicker,  setShowPicker]  = useState(false);
  const [tab,         setTab]         = useState('regime');
  const [name,        setName]        = useState('');
  const [description, setDescription] = useState('');
  const [sJson,       setSJson]       = useState(blankStrategy());
  const [saving,      setSaving]      = useState(false);
  const [saveMsg,     setSaveMsg]     = useState('');
  const [saveErr,     setSaveErr]     = useState('');
  const [openLevels,  setOpenLevels]  = useState(new Set());
  const [dragIdx,     setDragIdx]     = useState(null);
  const [openSignals, setOpenSignals] = useState(new Set());
  const [btCash,      setBtCash]      = useState('10000');
  const [btRunning,   setBtRunning]   = useState(false);
  const [btStatus,    setBtStatus]    = useState(null);
  const [btResults,   setBtResults]   = useState(null);
  // View window: width from y/m inputs (default 5y0m), start = left edge (ms).
  const [winY,        setWinY]        = useState(5);
  const [winM,        setWinM]        = useState(0);
  const [winStart,    setWinStart]    = useState(null); // ms; null until series loads
  const [showRegimeMarkers, setShowRegimeMarkers] = useState(true);
  const [showSignalMarkers, setShowSignalMarkers] = useState(true);
  const [showAllSignalFires, setShowAllSignalFires] = useState(false);
  const [showPortfolioData, setShowPortfolioData] = useState(true);
  const [showSignalLabels,  setShowSignalLabels]  = useState(true);
  const [hoverData,         setHoverData]         = useState(null); // day under cursor for pinned overlay
  const [lastDay,           setLastDay]           = useState(null); // last hovered/tapped backtest day (kept on mouse leave)
  const [chartJump,         setChartJump]         = useState(null); // {date} handed to the Signal Chart tab
  const [statsOpen,         setStatsOpen]         = useState(false); // mobile stats slide-out (desktop panel is always open)
  const [dragWin,           setDragWin]           = useState(null); // {start,width} in ms while the range band is dragged (commits on release)
  // Validation errors: Set of field keys that should render red. Keys:
  //   'cond:<condId>'   blank ticker on a signal condition
  //   'alloc:<allocId>' blank ticker on a regime allocation
  //   'sig:<sigId>'     signal-level error (target/conditions/duplicate)
  const [fieldErrors,       setFieldErrors]       = useState(new Set());
  const hasErr=k=>fieldErrors.has(k);
  // Saved signal/regime library (account-level, copy semantics)
  const [savedSignals, setSavedSignals] = useState([]); // [{id,name,updated_at}]
  const [savedRegimes, setSavedRegimes] = useState([]);
  const [libOpen,      setLibOpen]      = useState(null); // 'signals' | 'regimes' | null
  // Which tabs currently contain an errored field (for the ⚠ tab badge).
  const errorTabs=(()=>{
    const t=new Set();
    if(!fieldErrors.size)return t;
    sJson.regime_levels.forEach(lvl=>{
      lvl.allocations.forEach(a=>{if(fieldErrors.has(`alloc:${a.id}`))t.add('regime');});
    });
    sJson.signals.forEach(sig=>{
      if(fieldErrors.has(`sig:${sig.id}`))t.add('signals');
      sig.conditions.forEach(c=>{if(fieldErrors.has(`cond:${c.id}`))t.add('signals');});
    });
    return t;
  })();
  const [cmpTickers,  setCmpTickers]  = useState([]);
  const [cmpData,     setCmpData]     = useState({});
  const [cmpQ,        setCmpQ]        = useState('');
  const [cmpRes,      setCmpRes]      = useState([]);
  const [cmpOpen,     setCmpOpen]     = useState(false);
  const pollRef  = useRef(null);
  const canvasRef= useRef(null);
  const chartRef = useRef(null);
  const cmpTimer = useRef(null);
  const cmpWrap  = useRef(null);
  const progRef  = useRef(false);      // true while we set chart range programmatically
  const boundsRef= useRef({min:null,max:null}); // full series x-bounds (ms)
  const syncRef  = useRef(null);       // holds latest syncFromChart for zoom callbacks
  const dragRef  = useRef(null);       // latest dragWin values during a band drag, committed on release
  const lastDca  = useRef(null);       // last non-zero DCA amount; restored when the DCA checkbox is re-ticked


  useEffect(()=>{
    const h=e=>{if(cmpWrap.current&&!cmpWrap.current.contains(e.target))setCmpOpen(false);};
    document.addEventListener('mousedown',h);
    return()=>document.removeEventListener('mousedown',h);
  },[]);

  // Track the last non-zero DCA amount so the enable checkbox can restore it.
  useEffect(()=>{const v=Number(sJson.dca_amount);if(v>0)lastDca.current=v;},[sJson.dca_amount]);

  useEffect(()=>{
    api.get('/strategy/strategies').then(r=>setStrategies(r.data.strategies||[])).catch(()=>{}).finally(()=>setLoadingList(false));
    api.get('/lists').then(r=>setLists(r.data.lists||[])).catch(()=>{});
    refreshLibrary();
  },[]);

  const loadStrategy = useCallback(async id=>{
    try {
      const r=await api.get(`/strategy/strategies/${id}`);
      const s=r.data.strategy;
      setActiveId(s.id); setName(s.name); setDescription(s.description||''); setSaveMsg(''); setSaveErr(''); setTab('regime'); setShowPicker(false);
      const j=s.strategy_json||blankStrategy();
      const sorted=[...(j.regime_levels||[])].sort((a,b)=>a.level-b.level);
      const fallback=blankStrategy();
      const levels=(sorted.length?sorted:fallback.regime_levels).map((stored,i)=>({
        id:String(stored.id||uid()),
        level:i+1,
        label:stored.label||`Regime ${i+1}`,
        enabled:stored.enabled!==false,
        allocations:(stored.allocations||[]).map(deserialiseAlloc),
        dca_ticker:stored.dca_ticker||'CASH',
      }));
      const levelIds=new Set(levels.map(l=>l.id));
      const legacyTarget=sig=>{
        const jump=Number(sig.jump)||0;
        if(jump<Math.max(1,levels.length-1))return '';
        if(sig.direction==='bullish')return levels[0]?.id||'';
        if(sig.direction==='bearish')return levels[levels.length-1]?.id||'';
        return '';
      };
      const signals=(j.signals||[]).map((sig,i)=>({
        id:sig.id||uid(),label:sig.label||'',color:signalColor(sig,i),
        target_regime_id:levelIds.has(String(sig.target_regime_id||''))?String(sig.target_regime_id):legacyTarget(sig),
        direction:sig.direction||'bullish',type:sig.type||'conditions',
        smc:deserialiseSmc(sig.smc),conditions:(sig.conditions||[]).map(deserialiseCond),
      }));
      const startId=levelIds.has(String(j.starting_regime_id||''))?String(j.starting_regime_id):levels[levels.length-1].id;
      setSJson({backtest_timeframe:j.backtest_timeframe||'1d',base_currency:j.base_currency||'AUD',dca_amount:j.dca_amount||0,dca_frequency:j.dca_frequency||{n:1,unit:'months'},rebalance_every:j.rebalance_every||null,starting_regime_id:startId,regime_levels:levels,signals});
      setBtResults(null); setBtStatus(null); setLastDay(null);
      api.get(`/strategy/strategies/${id}/backtest/results`).then(r2=>setBtResults(r2.data)).catch(()=>{});
      api.get(`/strategy/strategies/${id}/backtest/status`).then(r2=>setBtStatus(r2.data.status)).catch(()=>{});
    } catch(e){console.error('Load failed',e);}
  },[]);

  const newStrategy=()=>{setActiveId(null);setName('');setDescription('');setSJson(blankStrategy());setBtResults(null);setBtStatus(null);setLastDay(null);setTab('regime');setSaveMsg('');setSaveErr('');setShowPicker(false);};

  // Returns {errors:Set<string>, messages:string[], tabs:Set<string>}.
  // Compares conditions as an UNORDERED set of ticker/lhs/op/rhs/streak tuples.
  const condKey=c=>JSON.stringify([c.ticker||'',c.timeframe||'1d',c.lhs,c.op,UNARY_OPS.has(c.op)?'':(c.rhs==='_num'?String(c.rhsNum||0):c.rhs),c.streak||1,MARGIN_OPS.has(c.op)?(Number(c.margin)||0):0,c.marginUnit||'pct',Number(c.lag)||0,Number(c.lhsOffset)||0,(UNARY_OPS.has(c.op)||condAgg(c))?0:(Number(c.rhsOffset)||0),condAgg(c)?c.rhsAgg:'none',condAgg(c)?(Number(c.rhsAggN)||0):0]);
  const condSetSig=conds=>JSON.stringify([...conds.map(condKey)].sort());
  const validateStrategy=()=>{
    const errors=new Set(); const messages=[]; const tabs=new Set();
    // Blank allocation tickers
    sJson.regime_levels.forEach(lvl=>{
      lvl.allocations.forEach(a=>{ const bad=(a.kind==='rank')?!a.listId:(!a.ticker||!a.ticker.trim()); if(bad){errors.add(`alloc:${a.id}`);tabs.add('regime');} });
    });
    // Validate intraday capabilities before saving.
    if(sJson.backtest_timeframe==='1m'){
      if(sJson.regime_levels.some(l=>l.allocations.some(a=>a.kind==='rank'))){errors.add('intraday:rank');tabs.add('regime');messages.push('Minute backtests do not support ranked-list allocations yet.');}
      if(sJson.signals.some(s=>s.type==='smc')){errors.add('intraday:smc');tabs.add('signals');messages.push('Minute backtests do not support SMC signals yet.');}
    }else if(sJson.signals.some(s=>s.conditions.some(c=>(c.timeframe||'1d')!=='1d'))){errors.add('intraday:mode');tabs.add('signals');messages.push('Choose 1-minute execution to use intraday conditions.');}
    // Every signal must target an existing regime. Condition signals also need
    // conditions; SMC signals need their structure ticker.
    const regimeIds=new Set(sJson.regime_levels.map(l=>l.id));
    sJson.signals.forEach(sig=>{
      if(!regimeIds.has(sig.target_regime_id)){errors.add(`sig:${sig.id}`);tabs.add('signals');messages.push(`Signal "${sig.label||'unnamed'}" needs a target regime.`);}
      if(sig.type==='smc'){
        if(!sig.smc.ticker||!sig.smc.ticker.trim()){errors.add(`smct:${sig.id}`);errors.add(`sig:${sig.id}`);tabs.add('signals');messages.push(`SMC signal "${sig.label||'unnamed'}" needs a ticker.`);}
        return;
      }
      if(!sig.conditions.length){ errors.add(`sig:${sig.id}`);tabs.add('signals');messages.push(`Signal "${sig.label||'unnamed'}" has no conditions.`); }
      sig.conditions.forEach(c=>{ if(!c.ticker||!c.ticker.trim()){errors.add(`cond:${c.id}`);tabs.add('signals');} });
    });
    if([...errors].some(k=>k.startsWith('cond:')||k.startsWith('alloc:'))) messages.push('One or more ticker fields are blank.');
    // Duplicate condition signals use the same unordered trigger set; target/label do not make the trigger distinct.
    // Skip signals with no conditions (already errored, and empty != empty here).
    const sigSeen={};
    sJson.signals.forEach(sig=>{
      if(sig.type==='smc'||!sig.conditions.length)return;
      const k=condSetSig(sig.conditions);
      if(sigSeen[k]){ errors.add(`sig:${sig.id}`);errors.add(`sig:${sigSeen[k]}`);tabs.add('signals'); if(!messages.includes('Two or more signals have identical conditions.'))messages.push('Two or more signals have identical conditions.'); }
      else sigSeen[k]=sig.id;
    });
    // Duplicate SMC signals (same ticker+trigger+direction — direction is part of
    // the trigger semantics for SMC, unlike condition signals).
    const smcSeen={};
    sJson.signals.forEach(sig=>{
      if(sig.type!=='smc'||!sig.smc.ticker)return;
      const k=JSON.stringify([sig.smc.ticker,sig.smc.trigger,sig.direction]);
      if(smcSeen[k]){ errors.add(`sig:${sig.id}`);errors.add(`sig:${smcSeen[k]}`);tabs.add('signals'); if(!messages.includes('Two or more SMC signals are identical.'))messages.push('Two or more SMC signals are identical.'); }
      else smcSeen[k]=sig.id;
    });
    return {errors,messages,tabs};
  };

  const saveStrategy=async()=>{
    if(!name.trim()){setSaveErr('Name required.');return false;}
    // Validate before saving
    const {errors,messages}=validateStrategy();
    setFieldErrors(errors);
    if(errors.size){ setSaveErr(messages.join(' ')||'Please fix the highlighted fields.'); setSaveMsg(''); return false; }
    setSaving(true);setSaveErr('');setSaveMsg('');
    const strategy_json={
      backtest_timeframe:sJson.backtest_timeframe||'1d',
      base_currency:sJson.base_currency,
      dca_amount:Number(sJson.dca_amount)||0,
      dca_frequency:sJson.dca_frequency,
      rebalance_every:sJson.rebalance_every||null,
      starting_regime_id:sJson.starting_regime_id,
      regime_levels:sJson.regime_levels.map(lvl=>({id:lvl.id,level:lvl.level,label:lvl.label,enabled:lvl.enabled!==false,allocations:lvl.allocations.map(serialiseAlloc),dca_ticker:lvl.dca_ticker})),
      signals:sJson.signals.map((sig,i)=>sig.type==='smc'
        ?{id:sig.id,label:sig.label,color:signalColor(sig,i),target_regime_id:sig.target_regime_id,direction:sig.direction,type:'smc',smc:serialiseSmc(sig.smc),conditions:[]}
        :{id:sig.id,label:sig.label,color:signalColor(sig,i),target_regime_id:sig.target_regime_id,type:'conditions',conditions:sig.conditions.map(serialiseCond)}),
    };
    try {
      if(activeId){
        await api.patch(`/strategy/strategies/${activeId}`,{name,description,strategy_json});
        setSaveMsg('Saved.'); setFieldErrors(new Set());
        setStrategies(prev=>prev.map(s=>s.id===activeId?{...s,name}:s));
      } else {
        const r=await api.post('/strategy/strategies',{name,description,strategy_json});
        const ns=r.data.strategy;
        setActiveId(ns.id);
        setStrategies(prev=>[ns,...prev]);
        setSaveMsg('Created.'); setFieldErrors(new Set());
      }
      return true;
    } catch(e){setSaveErr(e.response?.data?.error||'Save failed.');return false;}
    finally{setSaving(false);}
  };

  const deleteStrategy=async()=>{
    if(!activeId||!window.confirm('Delete this strategy?'))return;
    try{await api.delete(`/strategy/strategies/${activeId}`);setStrategies(prev=>prev.filter(s=>s.id!==activeId));newStrategy();}
    catch(e){setSaveErr(e.response?.data?.error||'Delete failed.');}
  };

  const toggleStrategyAlerts=async(strategy,e)=>{
    e.stopPropagation();
    if(!strategy.is_owner)return;
    const enabled=!strategy.signal_alerts_enabled;
    try{
      await api.patch(`/strategy/strategies/${strategy.id}/alerts`,{enabled});
      setStrategies(prev=>prev.map(s=>s.id===strategy.id?{...s,signal_alerts_enabled:enabled}:s));
      setSaveErr('');
      setSaveMsg(`Signal alerts ${enabled?'enabled':'disabled'} for "${strategy.name}".`);
    }catch(err){
      setSaveMsg('');
      setSaveErr(err.response?.data?.error||'Could not change signal alerts.');
    }
  };

  const triggerBacktest=async()=>{
    if(!activeId){setSaveErr('Save first.');return;}
    // The engine reads the SAVED strategy_json, so save and wait first — edits
    // (DCA amount/frequency, allocations, signals…) then always apply to the run.
    // On save failure saveStrategy has already shown the error; don't run.
    if(!await saveStrategy())return;
    setBtRunning(true);setBtStatus('pending');
    try{
      await api.post(`/strategy/strategies/${activeId}/backtest`,{start_date:null,initial_cash:Number(btCash)||10000});
      pollRef.current=setInterval(pollBacktest,2000);
    }catch(e){setBtRunning(false);setSaveErr(e.response?.data?.error||'Failed to start.');}
  };

  const pollBacktest=useCallback(async()=>{
    if(!activeId)return;
    try{
      const r=await api.get(`/strategy/strategies/${activeId}/backtest/status`);
      const st=r.data.status;
      setBtStatus(st);
      if(st==='complete'){clearInterval(pollRef.current);setBtRunning(false);const r2=await api.get(`/strategy/strategies/${activeId}/backtest/results`);setBtResults(r2.data);}
      else if(st==='error'){clearInterval(pollRef.current);setBtRunning(false);setSaveErr(r.data.error_msg?.split('\n').filter(Boolean).pop()||'Backtest error.');}
    }catch{clearInterval(pollRef.current);setBtRunning(false);}
  },[activeId]);

  useEffect(()=>()=>clearInterval(pollRef.current),[]);

  const handleCmpSearch=val=>{
    setCmpQ(val);clearTimeout(cmpTimer.current);
    if(!val.trim()){setCmpRes([]);setCmpOpen(false);return;}
    cmpTimer.current=setTimeout(async()=>{try{const r=await api.get('/chart/search',{params:{q:val}});setCmpRes(r.data||[]);setCmpOpen(true);}catch{setCmpRes([]);}},200);
  };

  const addCmpTicker=useCallback(async(sym,exch,tname)=>{
    const key=`${sym}:${exch}`;
    if(cmpTickers.find(t=>t.key===key))return;
    try{
      const r=await api.get('/compare/series',{params:{tickers:key}});
      const item=r.data.data?.[0];if(!item)return;
      setCmpTickers(prev=>{
        if(prev.some(t=>t.key===key))return prev;
        const palette=COLORS.slice(1); // reserve purple for the strategy
        const used=new Set(prev.map(t=>t.color.toLowerCase()));
        const color=palette.find(c=>!used.has(c.toLowerCase()))||palette[prev.length%palette.length];
        return [...prev,{key,name:tname||item.name,color}];
      });
      setCmpData(prev=>({...prev,[key]:item.series}));
    }
    catch{}
  },[cmpTickers]);

  const removeCmpTicker=key=>{setCmpTickers(prev=>prev.filter(t=>t.key!==key));setCmpData(prev=>{const n={...prev};delete n[key];return n;});};

  const buildChart=useCallback(()=>{
    const canvas=canvasRef.current;
    if(!canvas)return;
    if(chartRef.current){chartRef.current.destroy();chartRef.current=null;}
    if(!btResults?.result_json?.series?.length)return;
    const series=btResults.result_json.series;

    // Full series x-bounds, used to clamp the window and slider.
    const seriesMin=new Date(series[0].d).getTime();
    const seriesMax=new Date(series[series.length-1].d).getTime();
    boundsRef.current={min:seriesMin,max:seriesMax};

    // Window: width from y/m inputs, left edge = winStart (default to seriesMin).
    const widthMs=ymToMs(winY,winM);
    let wStart=winStart??seriesMin;
    // Clamp so the window sits within the series.
    const maxStart=Math.max(seriesMin,seriesMax-widthMs);
    if(wStart<seriesMin)wStart=seriesMin;
    if(wStart>maxStart)wStart=maxStart;
    let wEnd=wStart+widthMs;
    // Pad the right edge by a day so a point landing exactly on the axis max (e.g.
    // the final series point when the window reaches the end) isn't clipped at the
    // plot boundary. Never pad past one day beyond the last data point.
    const xAxisMax=Math.min(seriesMax,wEnd)+DAY_MS;

    // WINDOW-RELATIVE REPLAY. Every window position is an independent simulation
    // from the Initial Cash input: the balance starts at btCash on the window's
    // first day (first point at/after wStart); each later day applies the engine's
    // contribution-stripped growth (prevPv<=0 → factor 1), then that day's DCA
    // contribution lands on top — contributions show as visible steps and later
    // growth compounds the whole balance. Chart y = (balance/ini − 1)·100, so the
    // window's first day is always 0%. Points BEFORE the window get a backward
    // extension (invisible, but keeps the line continuous and the y-axis sane
    // while panning); y0 stashes the raw balance for rebaseLive. (Old results
    // without a contribution field just replay with zero contributions.)
    const ini=Number(btCash)||10000;
    const stratPts=series.map(d=>({
      x:new Date(d.d).getTime(),pv:d.portfolio_value,c:d.contribution||0,cash:d.cash,
      level:d.active_level,regime:d.regime_level,offset:d.momentum_offset,
      holdings:d.holdings||{},signals:d.signals_fired||[],rankings:d.rankings||null,
    }));
    // Daily growth factor vs the previous day (series entries are consecutive
    // trading days, so [i-1] is the prior bar).
    const stratG=stratPts.map((p,i)=>i>0&&stratPts[i-1].pv>0?(p.pv-p.c)/stratPts[i-1].pv:1);
    const k0=stratPts.findIndex(p=>p.x>=wStart);
    const k=k0<0?stratPts.length-1:k0;
    const stratBal=new Array(stratPts.length);
    stratBal[k]=ini;
    for(let i=k+1;i<stratPts.length;i++)stratBal[i]=stratBal[i-1]*stratG[i]+stratPts[i].c;
    for(let i=k;i>0;i--)stratBal[i-1]=stratG[i]>0?(stratBal[i]-stratPts[i].c)/stratG[i]:stratBal[i];
    // Historical runs may lack regime_changed; infer a day's net switch if needed.
    const startLevel=sJson.regime_levels.find(l=>l.id===sJson.starting_regime_id)?.level;
    const signalColors=Object.fromEntries(sJson.signals.map((s,i)=>[String(s.id),signalColor(s,i)]));
    const firedByDay=stratPts.map((p,i)=>{
      const previous=i>0?stratPts[i-1].regime:startLevel;
      const conflict=p.signals.includes('conflict');
      return p.signals.filter(s=>s&&typeof s==='object').map(s=>{
        const switched=typeof s.regime_changed==='boolean' ? s.regime_changed
          : !conflict&&previous!=null&&Number(p.regime)!==Number(previous)&&Number(s.target_regime??p.regime)===Number(p.regime);
        return {...s,regime_changed:switched,marker_color:signalColors[String(s.id)]||SIGNAL_COLORS[0]};
      });
    });
    const visibleFires=firedByDay.map(fires=>showAllSignalFires?fires:fires.filter(s=>s.regime_changed));
    const stratData=stratPts.map((p,i)=>({
      x:p.x,y0:stratBal[i],y:(stratBal[i]/ini-1)*100,
      meta:{pv:p.pv,level:p.level,regime:p.regime,offset:p.offset,cash:p.cash,holdings:p.holdings,signals:showSignalMarkers?visibleFires[i]:[],rankings:p.rankings},
    }));
    const datasets=[{label:name||'Strategy',data:stratData,borderColor:css('--accent'),backgroundColor:'transparent',borderWidth:2,pointRadius:0,pointHoverRadius:4,pointHoverBackgroundColor:css('--accent'),tension:0}];

    // Comparison tickers run the SAME replay on their own prices: balance starts
    // at Initial Cash on the ticker's first point inside the window, grows by its
    // daily price change, and receives the strategy's DCA schedule (same dates and
    // amounts). A scheduled contribution lands on the ticker's first trading day
    // on/after the date, so a cross-calendar holiday carries rather than drops the
    // cash. The anchor day's own contribution is excluded — the baseline, same
    // convention as the strategy line. (compare.py emits t as UTC-midnight ms, so
    // its ISO date matches the engine's d strings exactly.)
    const sched=Object.entries(series.reduce((m,d)=>{if(d.contribution)m[d.d]=(m[d.d]||0)+d.contribution;return m;},{})).sort((a,b)=>a[0]<b[0]?-1:1);
    cmpTickers.forEach(t=>{
      const raw=cmpData[t.key];if(!raw||!raw.length)return;
      const pts=raw.map(p=>({x:p.t,c:p.c,ds:new Date(p.t).toISOString().slice(0,10)}));
      const g=pts.map((p,i)=>i>0&&pts[i-1].c>0?p.c/pts[i-1].c:1);
      const ck0=pts.findIndex(p=>p.x>=wStart);
      const ck=ck0<0?pts.length-1:ck0;
      let si=0;while(si<sched.length&&sched[si][0]<=pts[ck].ds)si++;
      const bal=new Array(pts.length);
      bal[ck]=ini;
      for(let i=ck+1;i<pts.length;i++){
        let add=0;while(si<sched.length&&sched[si][0]<=pts[i].ds){add+=sched[si][1];si++;}
        bal[i]=bal[i-1]*g[i]+add;
      }
      for(let i=ck;i>0;i--)bal[i-1]=g[i]>0?bal[i]/g[i]:bal[i]; // cosmetic tail (before window)
      const finalData=pts.map((p,i)=>({x:p.x,y0:bal[i],y:(bal[i]/ini-1)*100}));
      datasets.push({label:t.key,data:finalData,borderColor:t.color,backgroundColor:'transparent',borderWidth:1.5,borderDash:[4,3],pointRadius:0,pointHoverRadius:0,tension:0});
    });

    // Cash allocation line: strategy cash as % of portfolio value, on its own
    // right-hand axis pinned to 0–100. Solid muted line, no fill (a band floods
    // the plot when cash sits high) — distinct from the dashed comparison
    // tickers by weight and solid stroke. No y0: rebaseLive skips it (rebasing
    // an allocation share is nonsense). Comparison tickers get no cash line.
    const cashData=stratPts.map(p=>({x:p.x,y:p.pv>0?(p.cash/p.pv)*100:0}));
    datasets.push({label:'Cash %',data:cashData,yAxisID:'yCash',borderColor:css('--text-muted'),backgroundColor:'transparent',borderWidth:1.5,pointRadius:0,pointHoverRadius:0,tension:0});

    // Render each signal in its selected colour, with fill based on switching.
    const annotations={};
    let prevRegime=startLevel??null;
    stratPts.forEach((p,i)=>{
      const yVal=stratData[i]?.y;
      const inWin=p.x>=wStart&&p.x<=wEnd;

      // Regime-change marker (small circle, level number)
      if(showRegimeMarkers && inWin && p.regime!==prevRegime && prevRegime!==null){
        annotations[`rg_${i}`]={
          type:'label',
          xValue:p.x, yValue:yVal,
          content:[String(p.regime)],
          font:{size:10,family:"'JetBrains Mono',monospace",weight:'700'},
          color:levelBorder(p.regime),
          backgroundColor:css('--surface'),
          borderColor:levelBorder(p.regime),
          borderWidth:2,
          borderRadius:20,
          padding:{top:2,bottom:2,left:5,right:5},
          drawTime:'afterDatasetsDraw',
        };
      }

      if(showSignalMarkers && inWin){
        const fired=visibleFires[i];
        fired.forEach((signal,j)=>{
          annotations[`sig_${i}_${j}`]={
            type:'label',
            xValue:p.x, yValue:yVal,
            content:[signal.regime_changed?'▶':'▷'],
            color:signal.marker_color,
            font:{size:17,family:"'JetBrains Mono',monospace",weight:'700'},
            backgroundColor:'transparent',
            padding:0,
            xAdjust:(j-(fired.length-1)/2)*16,
            drawTime:'afterDatasetsDraw',
          };
        });
      }

      prevRegime=p.regime;
    });

    // End-of-line labels: tag each dataset's final VISIBLE point with its %, in
    // the line's own colour, right-aligned just inside the plot's right edge.
    // Labels are sorted by pixel height and nudged apart (≥14px) so lines ending
    // close together stay legible. The cash label is excluded — different axis,
    // different units — and drawn at its own y. Runs on every draw, so it
    // tracks pan/zoom.
    const endLabels={
      id:'endLabels',
      afterDatasetsDraw(chart){
        const area=chart.chartArea;if(!area)return;
        const items=[];
        let cashLabel=null; // cash is on its own 0–100 axis: drawn at its own y, kept out of the % nudge queue
        chart.data.datasets.forEach((ds,di)=>{
          if(chart.getDatasetMeta(di).hidden)return;
          const ys=chart.scales[ds.yAxisID||'y'];
          for(let i=ds.data.length-1;i>=0;i--){
            const p=ds.data[i];
            if(p.x>=chart.scales.x.min&&p.x<=chart.scales.x.max&&Number.isFinite(p.y)){
              const it={py:ys.getPixelForValue(p.y),txt:(ds.yAxisID==='yCash'?'Cash ':(p.y>=0?'+':''))+p.y.toFixed(1)+'%',clr:ds.borderColor};
              if(ds.yAxisID==='yCash')cashLabel=it;else items.push(it);
              break;
            }
          }
        });
        if(!items.length&&!cashLabel)return;
        items.sort((a,b)=>a.py-b.py);
        const GAP=14;
        if(items.length){
          items[0].py=Math.max(items[0].py,area.top+6);
          for(let i=1;i<items.length;i++)items[i].py=Math.max(items[i].py,items[i-1].py+GAP);
          const over=items[items.length-1].py-(area.bottom-6);
          if(over>0){const up=Math.min(over,items[0].py-(area.top+6));items.forEach(it=>{it.py-=up;});} // shift up, but never past the top clamp
        }
        const ctx=chart.ctx;
        ctx.save();
        ctx.font="700 9px 'JetBrains Mono',monospace";
        ctx.textAlign='right';
        ctx.textBaseline='middle';
        items.forEach(it=>{ctx.fillStyle=it.clr;ctx.fillText(it.txt,area.right-4,it.py);});
        if(cashLabel){ctx.fillStyle=cashLabel.clr;ctx.fillText(cashLabel.txt,area.right-4,Math.min(Math.max(cashLabel.py,area.top+6),area.bottom-6));}
        ctx.restore();
      },
    };
    chartRef.current=new Chart(canvas,{
      type:'line',data:{datasets},plugins:[endLabels],
      options:{
        animation:false,responsive:true,maintainAspectRatio:false,parsing:false,
        interaction:{mode:'index',intersect:false},
        scales:{
          x:{type:'time',time:{unit:'month',tooltipFormat:'dd MMM yyyy'},min:wStart,max:xAxisMax,grid:{color:css('--grid')},ticks:{color:css('--text-muted'),font:{family:"'JetBrains Mono',monospace",size:10},maxRotation:0,maxTicksLimit:8}},
          y:{grid:{color:css('--grid')},ticks:{color:css('--text-muted'),font:{family:"'JetBrains Mono',monospace",size:10},callback:v=>(v>=0?'+':'')+v.toFixed(1)+'%'}},
          // Right-hand axis for the cash line only: fixed 0–100%, no gridlines.
          yCash:{position:'right',min:0,max:100,grid:{drawOnChartArea:false},ticks:{color:css('--text-faint'),font:{family:"'JetBrains Mono',monospace",size:9},maxTicksLimit:6,callback:v=>v+'%'}},
        },
        // onHover updates the pinned top-left portfolio overlay to the day under the
        // cursor (follows the x-axis). Reads the strategy dataset point's meta.
        onHover:(evt,activeEls,chart)=>{
          if(!activeEls||!activeEls.length){return;}
          // Prefer the strategy dataset (index 0) point at this x-position
          const el=activeEls.find(a=>a.datasetIndex===0)||activeEls[0];
          const pt=chart.data.datasets[el.datasetIndex]?.data[el.index];
          const m=pt?.meta;
          if(m){setHoverData({x:pt.x,...m});setLastDay(new Date(pt.x).toISOString().slice(0,10));}
        },
        plugins:{
          legend:{display:false},
          // Built-in tooltip is now used ONLY to show the signal name(s) on signal
          // triangle markers. The portfolio composition has moved to the pinned
          // top-left overlay. Toggled by Signal_labels.
          tooltip:{
            enabled:showSignalLabels,
            filter:item=>item.datasetIndex===0, // only the strategy line carries signals
            callbacks:{
              title:()=>'',
              label:ctx=>{
                const m=ctx.raw?.meta;
                if(!m||!m.signals||!m.signals.length)return '';
                const fired=m.signals.filter(s=>typeof s==='object');
                if(!fired.length)return '';
                return fired.map(s=>`${s.regime_changed?'▶':'▷'} ${(s.label&&s.label.trim())?s.label:'missing_label'}`);
              },
            },
          },
          annotation:{annotations},
          zoom:{
            pan:{enabled:true,mode:'x',onPanComplete:({chart})=>syncRef.current&&syncRef.current(chart)},
            zoom:{wheel:{enabled:true},pinch:{enabled:true},mode:'x',onZoomComplete:({chart})=>syncRef.current&&syncRef.current(chart)},
          },
        },
      },
    });
  },[btResults,cmpTickers,cmpData,winY,winM,winStart,name,sJson,btCash,showRegimeMarkers,showSignalMarkers,showAllSignalFires,showSignalLabels]);

  useEffect(()=>{buildChart();return()=>{if(chartRef.current){chartRef.current.destroy();chartRef.current=null;}};},[buildChart]);

  // Re-rebase a live chart to its current visible x-min and refresh, WITHOUT a full
  // rebuild — used after pan/zoom so the left edge snaps to 0%. The cash line is
  // skipped: it's an allocation share, not a rebased return.
  const rebaseLive=useCallback((chart)=>{
    const xMin=chart.scales.x.min;
    chart.data.datasets.forEach(ds=>{
      if(ds.yAxisID==='yCash')return;
      const first=ds.data.find(pt=>pt.x>=xMin)||ds.data[0];
      const base=(first&&first.y0)||1;
      ds.data.forEach(pt=>{pt.y=((pt.y0-base)/base)*100;});
    });
    chart.update('none');
  },[]);

  // After a user pan/zoom: read the chart's actual visible range, write it back into
  // the window state (y/m inputs + slider), then rebase the left edge to 0%.
  const syncFromChart=useCallback((chart)=>{
    if(progRef.current)return; // ignore range changes we made programmatically
    const xMin=chart.scales.x.min, xMax=chart.scales.x.max;
    const {y,m}=msToYM(xMax-xMin);
    const cy=Math.max(0,y), cm=Math.max(0,m);
    setWinY(cy); setWinM((cy===0&&cm===0)?1:cm);
    setWinStart(xMin);
    rebaseLive(chart);
  },[rebaseLive]);
  syncRef.current=syncFromChart;

  // When a fresh backtest result loads, reset the window left edge to the series
  // start (so the default 5y window begins at the earliest available data).
  useEffect(()=>{
    if(btResults?.result_json?.series?.length){
      setWinStart(new Date(btResults.result_json.series[0].d).getTime());
    }
  },[btResults]);

  // y/m input change: enforce never-0/0 (if y=0 then m>=1), update width. The chart
  // rebuild (winY/winM deps) re-clamps the window and rebases.
  const setWindowYM=(y,m)=>{
    y=Math.max(0,Math.floor(Number(y)||0));
    m=Math.max(0,Math.floor(Number(m)||0));
    if(y===0&&m===0)m=1;
    if(m>11&&y>0)m=11; // months field is 0..11; whole years go in the year field
    setWinY(y); setWinM(m);
  };

  const stats=(()=>{
    if(!btResults?.result_json?.series?.length)return null;
    const s=btResults.result_json.series;
    const sMin=new Date(s[0].d).getTime(), sMax=new Date(s[s.length-1].d).getTime();
    const width=ymToMs(winY,winM);
    let ws=winStart??sMin;
    const maxStart=Math.max(sMin,sMax-width);
    if(ws<sMin)ws=sMin; if(ws>maxStart)ws=maxStart;
    const we=ws+width;
    const win=s.filter(d=>{const t=new Date(d.d).getTime();return t>=ws&&t<=we;});
    const slice=win.length?win:s;
    // The window is an INDEPENDENT simulation from the Initial Cash input — the
    // same replay the chart draws: balance starts at ini on the window's first
    // day, each later day applies contribution-stripped growth (prev<=0 → factor
    // 1), then that day's contribution lands. Final is the replayed end balance;
    // Contributions excludes the first day (baseline); Growth = Final − all cash
    // put in. Return is MONEY-WEIGHTED (gain over cash actually deployed). CAGR
    // stays TIME-WEIGHTED (asset performance regardless of contributions) — twr
    // reuses the replay's growth factors, so the two can never disagree.
    const ini=Number(btCash)||10000;
    let bal=ini,contrib=0,twr=1;
    for(let i=1;i<slice.length;i++){
      const prev=slice[i-1].portfolio_value;
      const c=slice[i].contribution||0;
      const g=prev>0?(slice[i].portfolio_value-c)/prev:1;
      bal=bal*g+c;contrib+=c;twr*=g;
    }
    const fin=bal;
    const growth=fin-ini-contrib;
    const deployed=ini+contrib;
    const ret=deployed>0?(growth/deployed)*100:0;
    const tradesInWin=(btResults.result_json.trades||[]).filter(t=>{const x=new Date(t.d).getTime();return x>=ws&&x<=we;}).length;
    // Window CAGR: annualised over the window's actual date span. Null when the
    // window is too short to annualise (single point / zero span).
    const yrs=(new Date(slice[slice.length-1].d).getTime()-new Date(slice[0].d).getTime())/YEAR_MS;
    const cagr=(twr>0&&yrs>0)?(Math.pow(twr,1/yrs)-1)*100:null;
    return{ini,contrib,fin,growth,ret,cagr,trades:tradesInWin,days:slice.length};
  })();

  // Per-comparison-ticker window stats: the SAME replay the chart draws for the
  // ticker's line (anchor at Initial Cash on the first in-window point, daily
  // price growth + the strategy's DCA schedule), so these match the chart line.
  const cmpStats=(()=>{
    if(!btResults?.result_json?.series?.length)return[];
    const s=btResults.result_json.series;
    const sMin=new Date(s[0].d).getTime(), sMax=new Date(s[s.length-1].d).getTime();
    const width=ymToMs(winY,winM);
    let ws=winStart??sMin;
    const maxStart=Math.max(sMin,sMax-width);
    if(ws<sMin)ws=sMin; if(ws>maxStart)ws=maxStart;
    const we=ws+width;
    const ini=Number(btCash)||10000;
    const sched=Object.entries(s.reduce((m,d)=>{if(d.contribution)m[d.d]=(m[d.d]||0)+d.contribution;return m;},{})).sort((a,b)=>a[0]<b[0]?-1:1);
    return cmpTickers.map(t=>{
      const raw=cmpData[t.key];
      const pts=(raw||[]).map(p=>({x:p.t,c:p.c,ds:new Date(p.t).toISOString().slice(0,10)})).filter(p=>p.x>=ws&&p.x<=we);
      if(pts.length<2)return null;
      let si=0;while(si<sched.length&&sched[si][0]<=pts[0].ds)si++;
      let bal=ini,contrib=0,twr=1;
      for(let i=1;i<pts.length;i++){
        const g=pts[i-1].c>0?pts[i].c/pts[i-1].c:1;
        let add=0;while(si<sched.length&&sched[si][0]<=pts[i].ds){add+=sched[si][1];si++;}
        bal=bal*g+add;contrib+=add;twr*=g;
      }
      const growth=bal-ini-contrib;
      const deployed=ini+contrib;
      const ret=deployed>0?(growth/deployed)*100:0;
      const yrs=(pts[pts.length-1].x-pts[0].x)/YEAR_MS;
      const cagr=(twr>0&&yrs>0)?(Math.pow(twr,1/yrs)-1)*100:null;
      return{t,st:{ini,contrib,fin:bal,growth,ret,cagr}};
    }).filter(Boolean);
  })();

  // ── Level/signal helpers ──────────────────────────────────────────────────
  const updLevel =(idx,fn)=>setSJson(p=>({...p,regime_levels:p.regime_levels.map((l,i)=>i===idx?fn(l):l)}));
  const addAlloc =(idx)=>updLevel(idx,l=>({...l,allocations:[...l.allocations,{id:uid(),kind:'fixed',ticker:'',pct:0}]}));
  const remAlloc =(idx,aid)=>updLevel(idx,l=>({...l,allocations:l.allocations.filter(a=>a.id!==aid)}));
  const updAlloc =(idx,aid,patch)=>updLevel(idx,l=>({...l,allocations:l.allocations.map(a=>a.id===aid?{...a,...patch}:a)}));
  const addSig   =()=>{const s={...blankSignal(),color:SIGNAL_COLORS[sJson.signals.length%SIGNAL_COLORS.length]};setSJson(p=>({...p,signals:[...p.signals,s]}));setOpenSignals(p=>new Set([...p,s.id]));};
  const remSig   =sid=>setSJson(p=>({...p,signals:p.signals.filter(s=>s.id!==sid)}));
  const updSig   =(sid,f,v)=>setSJson(p=>({...p,signals:p.signals.map(s=>s.id===sid?{...s,[f]:v}:s)}));
  const addSC    =sid=>setSJson(p=>({...p,signals:p.signals.map(s=>s.id===sid?{...s,conditions:[...s.conditions,blankCond()]}:s)}));
  const remSC    =(sid,cid)=>setSJson(p=>({...p,signals:p.signals.map(s=>s.id===sid?{...s,conditions:s.conditions.filter(c=>c.id!==cid)}:s)}));
  const updSC    =(sid,cid,nc)=>setSJson(p=>({...p,signals:p.signals.map(s=>s.id===sid?{...s,conditions:s.conditions.map(c=>c.id===cid?nc:c)}:s)}));
  const togSig   =sid=>setOpenSignals(p=>{const n=new Set(p);n.has(sid)?n.delete(sid):n.add(sid);return n;});
  const togLvl   =id=>setOpenLevels(p=>{const s=new Set(p);s.has(id)?s.delete(id):s.add(id);return s;});
  // Add a new level at the end, renumbered 1..N
  const addLevelItem=()=>setSJson(p=>{const lv=[...p.regime_levels,blankLevel(p.regime_levels.length+1)];return{...p,regime_levels:lv.map((l,i)=>({...l,level:i+1}))};});
  // Remove a level by id, renumber the rest
  const remLevelItem=(id)=>setSJson(p=>{
    const kept=p.regime_levels.filter(l=>l.id!==id);
    const lv=(kept.length?kept:[blankLevel(1)]).map((l,i)=>({...l,level:i+1}));
    const ids=new Set(lv.map(l=>l.id));
    return {...p,regime_levels:lv,
      starting_regime_id:ids.has(p.starting_regime_id)?p.starting_regime_id:lv[lv.length-1].id,
      signals:p.signals.map(s=>ids.has(s.target_regime_id)?s:{...s,target_regime_id:''})};
  });
  // Reorder: move level from index `from` to index `to`, renumber 1..N
  const moveLevelItem=(from,to)=>setSJson(p=>{const lv=[...p.regime_levels];const [m]=lv.splice(from,1);lv.splice(to,0,m);return{...p,regime_levels:lv.map((l,i)=>({...l,level:i+1}))};});

  // ── Saved signal/regime library ───────────────────────────────────────────
  const refreshLibrary=()=>{
    api.get('/strategy/saved-signals').then(r=>setSavedSignals(r.data.saved_signals||[])).catch(()=>{});
    api.get('/strategy/saved-regimes').then(r=>setSavedRegimes(r.data.saved_regimes||[])).catch(()=>{});
  };

  // Validate one item before saving it to the library (no blank tickers; signals
  // need at least one condition). Returns an error string or null.
  const validateSignalForLibrary=sig=>{
    if(!sJson.regime_levels.some(l=>l.id===sig.target_regime_id))return 'Signal needs a target regime.';
    if(sig.type==='smc')return(!sig.smc.ticker||!sig.smc.ticker.trim())?'SMC signal needs a ticker.':null;
    if(!sig.conditions.length)return 'Signal has no conditions.';
    if(sig.conditions.some(c=>!c.ticker||!c.ticker.trim()))return 'Signal has a blank ticker.';
    return null;
  };
  const validateRegimeForLibrary=lvl=>{
    if(lvl.allocations.some(a=>(a.kind==='rank'?!a.listId:(!a.ticker||!a.ticker.trim()))))return 'Regime has a blank allocation ticker.';
    return null;
  };

  // Save a signal to the library using its label as the name.
  const saveSignalToLib=async(sig)=>{
    const nm=(sig.label||'').trim();
    if(!nm){setFieldErrors(new Set([`signame:${sig.id}`]));setSaveErr('Name the signal (its label) before saving it to the library.');return;}
    const err=validateSignalForLibrary(sig);
    if(err){setSaveErr(err);return;}
    const targetLevel=sJson.regime_levels.find(l=>l.id===sig.target_regime_id)?.level;
    const signal_json=sig.type==='smc'
      ?{label:sig.label,color:sig.color,target_regime_level:targetLevel,direction:sig.direction,type:'smc',smc:serialiseSmc(sig.smc),conditions:[]}
      :{label:sig.label,color:sig.color,target_regime_level:targetLevel,type:'conditions',conditions:sig.conditions.map(serialiseCond)};
    const post=ov=>api.post('/strategy/saved-signals',{name:nm,signal_json,overwrite:ov});
    try{
      await post(false);
      setSaveErr('');setSaveMsg(`Signal "${nm}" saved to library.`);refreshLibrary();
    }catch(e){
      if(e.response?.status===409){
        if(window.confirm(`A saved signal named "${nm}" already exists. Overwrite it?`)){
          try{await post(true);setSaveErr('');setSaveMsg(`Signal "${nm}" overwritten.`);refreshLibrary();}
          catch{setSaveErr('Save failed.');}
        }
      }else{setSaveErr(e.response?.data?.error||'Save failed.');}
    }
  };

  // Save a regime level to the library using its label as the name.
  const saveRegimeToLib=async(lvl)=>{
    const nm=(lvl.label||'').trim();
    if(!nm){setFieldErrors(new Set([`lvlname:${lvl.id}`]));setSaveErr('Name the level (its label) before saving it to the library.');return;}
    const err=validateRegimeForLibrary(lvl);
    if(err){setSaveErr(err);return;}
    const regime_json={label:lvl.label,enabled:lvl.enabled!==false,allocations:lvl.allocations.map(serialiseAlloc),dca_ticker:lvl.dca_ticker};
    const post=ov=>api.post('/strategy/saved-regimes',{name:nm,regime_json,overwrite:ov});
    try{
      await post(false);
      setSaveErr('');setSaveMsg(`Regime "${nm}" saved to library.`);refreshLibrary();
    }catch(e){
      if(e.response?.status===409){
        if(window.confirm(`A saved regime named "${nm}" already exists. Overwrite it?`)){
          try{await post(true);setSaveErr('');setSaveMsg(`Regime "${nm}" overwritten.`);refreshLibrary();}
          catch{setSaveErr('Save failed.');}
        }
      }else{setSaveErr(e.response?.data?.error||'Save failed.');}
    }
  };

  // Load a saved signal as a fresh copy appended to the current strategy.
  const loadSignalFromLib=async(itemId)=>{
    try{
      const r=await api.get(`/strategy/saved-signals/${itemId}`);
      const j=r.data.saved_signal?.signal_json;if(!j)return;
      let target=sJson.regime_levels.find(l=>l.level===Number(j.target_regime_level))?.id||'';
      if(!target&&Number(j.jump)>=Math.max(1,sJson.regime_levels.length-1)){
        target=j.direction==='bullish'?sJson.regime_levels[0]?.id||'':j.direction==='bearish'?sJson.regime_levels[sJson.regime_levels.length-1]?.id||'':'';
      }
      const ns={id:uid(),label:j.label||'',color:signalColor(j,sJson.signals.length),target_regime_id:target,direction:j.direction||'bullish',type:j.type||'conditions',smc:deserialiseSmc(j.smc),conditions:(j.conditions||[]).map(deserialiseCond)};
      setSJson(p=>({...p,signals:[...p.signals,ns]}));
      setOpenSignals(p=>new Set([...p,ns.id]));
      setLibOpen(null);setSaveMsg(`Loaded signal "${ns.label||'unnamed'}".`);
    }catch{setSaveErr('Load failed.');}
  };

  // Load a saved regime as a fresh copy appended as a new level (renumbered 1..N).
  const loadRegimeFromLib=async(itemId)=>{
    try{
      const r=await api.get(`/strategy/saved-regimes/${itemId}`);
      const j=r.data.saved_regime?.regime_json;if(!j)return;
      const nl={id:uid(),label:j.label||'',enabled:j.enabled!==false,allocations:(j.allocations||[]).map(deserialiseAlloc),dca_ticker:j.dca_ticker||'CASH'};
      setSJson(p=>{const lv=[...p.regime_levels,nl];return{...p,regime_levels:lv.map((l,i)=>({...l,level:i+1}))};});
      setOpenLevels(p=>new Set([...p,nl.id]));
      setLibOpen(null);setSaveMsg(`Loaded regime "${nl.label||'unnamed'}".`);
    }catch{setSaveErr('Load failed.');}
  };

  // Delete a library item (with the required confirmation).
  const deleteLibItem=async(kind,itemId,nm)=>{
    const what=kind==='signals'?'signal':'regime';
    if(!window.confirm(`Delete "${nm}" from the library permanently? Any strategy using it will retain their copy, but you will no longer be able to import this ${what} into other strategies.`))return;
    try{
      await api.delete(`/strategy/saved-${kind==='signals'?'signals':'regimes'}/${itemId}`);
      refreshLibrary();
    }catch{setSaveErr('Delete failed.');}
  };

  const activeName=strategies.find(s=>s.id===activeId)?.name||(activeId?name:null);
  const regimeForId=id=>sJson.regime_levels.find(l=>l.id===id);

  // ── Styles ────────────────────────────────────────────────────────────────
  const lp={padding:'0 10px 10px',borderTop:'1px solid var(--border)',display:'flex',flexDirection:'column',gap:'10px'};
  const sec={background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius)',marginBottom:'10px'};
  const sHdr={padding:'8px 12px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',cursor:'pointer',userSelect:'none'};
  const sTtl={fontSize:'11px',fontWeight:700,color:'var(--text)',fontFamily:"'JetBrains Mono',monospace",textTransform:'uppercase'};
  const sBody={padding:'10px 12px'};
  const inpW={padding:'6px 8px',fontSize:'12px',border:'1px solid var(--border)',borderRadius:'var(--radius)',background:'var(--bg)',color:'var(--text)',fontFamily:"'JetBrains Mono',monospace",width:'100%',boxSizing:'border-box'};
  const inp={padding:'5px 8px',fontSize:'12px',border:'1px solid var(--border)',borderRadius:'var(--radius)',background:'var(--bg)',color:'var(--text)',fontFamily:"'JetBrains Mono',monospace"};
  const sel={padding:'5px 6px',fontSize:'12px',border:'1px solid var(--border)',borderRadius:'var(--radius)',background:'var(--bg)',color:'var(--text)',fontFamily:"'JetBrains Mono',monospace"};
  const addB={padding:'3px 10px',fontSize:'11px',border:'none',borderRadius:'20px',background:'var(--accent)',color:'var(--on-accent)',cursor:'pointer',fontFamily:"'JetBrains Mono',monospace",fontWeight:600,flexShrink:0};
  const btn={background:'var(--accent)',color:'var(--on-accent)',border:'none',borderRadius:'var(--radius)',padding:'8px 18px',fontSize:'13px',fontWeight:600,cursor:'pointer'};
  const btnG={background:'none',color:'var(--text-muted)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'5px 12px',fontSize:'12px',cursor:'pointer'};
  const btnD={background:'none',color:'var(--error)',border:'1px solid var(--error)',borderRadius:'var(--radius)',padding:'5px 12px',fontSize:'12px',cursor:'pointer'};
  const lvlRow=(a)=>({border:'1px solid var(--border)',borderRadius:'var(--radius)',marginBottom:'6px',background:a?'var(--bg)':'var(--surface)'});
  const lvlHdr={padding:'7px 10px',display:'flex',alignItems:'center',gap:'6px',cursor:'pointer',userSelect:'none'};
  const targetB={fontSize:'10px',padding:'2px 7px',borderRadius:'3px',background:'var(--accent-dim)',color:'var(--accent)',fontWeight:700,fontFamily:"'JetBrains Mono',monospace",flexShrink:0};
  const pill=(c)=>({display:'inline-flex',alignItems:'center',gap:'5px',padding:'3px 8px',borderRadius:'12px',border:`1px solid ${c}`,fontSize:'11px',fontFamily:"'JetBrains Mono',monospace",background:'var(--bg)',flexShrink:0});
  const statLbl={fontSize:'9px',color:'var(--text-muted)',fontFamily:"'JetBrains Mono',monospace",textTransform:'uppercase',whiteSpace:'nowrap'};
  const lbl={fontSize:'10px',fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',fontFamily:"'JetBrains Mono',monospace",display:'block',marginBottom:'3px'};
  const statVal={fontSize:'12px',fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:'var(--text)',whiteSpace:'nowrap'};

  // Stat chip + panel content, shared by the desktop right panel and the mobile
  // slide-out so both render identical numbers.
  const chip=(l,v,title,clr)=>(
    <div key={l} style={{display:'flex',flexDirection:'column',gap:'1px'}} title={title}>
      <span style={statLbl}>{l}</span><span style={{...statVal,...(clr?{color:clr}:{})}}>{v}</span>
    </div>
  );
  const moneyStats=st=>[
    chip('Initial',fmt$(st.ini),"Starting balance: the Initial Cash input on the window's first day"),
    chip('Contributions',fmt$(st.contrib),"DCA contributions within the window (the first day is the baseline)"),
    chip('Final',fmt$(st.fin),"End-of-window balance from the window replay"),
    chip('Growth',fmt$(st.growth),"Final − Initial − Contributions: investment gain only, contributed cash excluded",st.growth>=0?'var(--up)':'var(--down)'),
    chip('Return',fmtPct(st.ret),"Money-weighted return: Growth ÷ (Initial + Contributions)",st.ret>=0?'var(--up)':'var(--down)'),
    chip('CAGR',st.cagr==null?'—':fmtPct(st.cagr)+'/yr',"Compound annual growth rate over the selected window (time-weighted)",st.cagr==null?null:(st.cagr>=0?'var(--up)':'var(--down)')),
  ];
  const statsContent=stats&&(
    <>
      <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
        <span style={{...statLbl,color:'var(--accent)',fontWeight:700}}>{name||'Strategy'}</span>
        {moneyStats(stats)}
        {chip('Trades',stats.trades)}
        {chip('Days',stats.days)}
        {chip('Period',`${btResults.computed_from} → ${btResults.computed_to}`)}
      </div>
      {cmpStats.map(({t,st})=>(
        <div key={t.key} style={{display:'flex',flexDirection:'column',gap:'10px',borderTop:'1px solid var(--border)',paddingTop:'10px'}}>
          <span style={{...statLbl,color:t.color,fontWeight:700}}>{t.key}</span>
          {moneyStats(st)}
        </div>
      ))}
    </>
  );

  return (
    <div style={{height:'100dvh',background:'var(--bg)',display:'flex',flexDirection:'column',overflow:'hidden'}}>
      {/* Mobile strategy picker */}
      {isMobile&&showPicker&&(
        <div style={{position:'fixed',inset:0,background: 'var(--overlay)',zIndex:300,display:'flex',flexDirection:'column',justifyContent:'flex-end'}} onClick={()=>setShowPicker(false)}>
          <div style={{background:'var(--surface)',borderRadius:'16px 16px 0 0',padding:'16px',maxHeight:'70vh',overflowY:'auto'}} onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'12px'}}>
              <span style={{fontWeight:700,fontSize:'14px'}}>Select Strategy</span>
              <button style={{background:'none',border:'none',fontSize:'20px',cursor:'pointer',color:'var(--text-muted)'}} onClick={()=>setShowPicker(false)}>×</button>
            </div>
            <button style={{...btn,width:'100%',marginBottom:'8px',background:'var(--accent-dim)',color:'var(--accent)',border:'1px solid var(--accent)'}} onClick={newStrategy}>+ New Strategy</button>
            {strategies.map(s=>(
              <div key={s.id} style={{padding:'12px',border:'1px solid var(--border)',borderRadius:'var(--radius)',marginBottom:'6px',cursor:'pointer',background:s.id===activeId?'var(--accent-dim)':'var(--bg)'}} onClick={()=>loadStrategy(s.id)}>
                <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                  <div style={{fontWeight:600,fontSize:'13px',color:s.id===activeId?'var(--accent)':'var(--text)',flex:1,minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.name}</div>
                  {s.is_owner&&user?.is_admin&&(
                    <button
                      onClick={e=>toggleStrategyAlerts(s,e)}
                      title={s.signal_alerts_enabled?'Signal alerts enabled — tap to disable':'Enable Discord signal alerts'}
                      style={{padding:'3px 8px',fontSize:'9px',fontWeight:700,fontFamily:"'JetBrains Mono',monospace",borderRadius:'10px',cursor:'pointer',border:`1px solid ${s.signal_alerts_enabled?'var(--success)':'var(--border)'}`,background:s.signal_alerts_enabled?'var(--success)':'transparent',color:s.signal_alerts_enabled?'var(--on-accent)':'var(--text-muted)',flexShrink:0}}
                    >ALERT {s.signal_alerts_enabled?'ON':'OFF'}</button>
                  )}
                </div>
                <div style={{fontSize:'11px',color:'var(--text-muted)',fontFamily:"'JetBrains Mono',monospace"}}>{s.updated_at?new Date(s.updated_at).toLocaleDateString():''}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Nav */}
      <Navbar compact hideUser={isMobile}>
        {isMobile&&(
          <button style={{flex:1,margin:'0 8px',padding:'6px 10px',border:'1px solid var(--border)',borderRadius:'var(--radius)',background:'var(--bg)',color:'var(--text)',fontSize:'12px',cursor:'pointer',textAlign:'left',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} onClick={()=>setShowPicker(true)}>
            {activeName||'— Select strategy —'} ▾
          </button>
        )}
      </Navbar>

      <div style={{flex:1,display:'flex',overflow:'hidden',minHeight:0}}>
        {/* Desktop left panel */}
        {!isMobile&&(
          <div style={{width:'220px',flexShrink:0,borderRight:'1px solid var(--border)',background:'var(--surface)',display:'flex',flexDirection:'column',overflow:'hidden'}}>
            <div style={{padding:'10px 12px 8px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
              <span style={{fontSize:'10px',fontWeight:700,color:'var(--text-muted)',fontFamily:"'JetBrains Mono',monospace",textTransform:'uppercase'}}>Strategies</span>
              <button style={addB} onClick={newStrategy}>+ New</button>
            </div>
            <div style={{flex:1,overflowY:'auto'}}>
              {loadingList&&<div style={{padding:'14px',fontSize:'12px',color:'var(--text-muted)'}}>Loading…</div>}
              {!loadingList&&strategies.length===0&&<div style={{padding:'14px',fontSize:'12px',color:'var(--text-muted)'}}>No strategies yet.</div>}
              {strategies.map(s=>(
                <div key={s.id} style={{padding:'8px 12px',cursor:'pointer',borderLeft:`3px solid ${s.id===activeId?'var(--accent)':'transparent'}`,background:s.id===activeId?'var(--accent-dim)':'transparent',fontSize:'12px',color:s.id===activeId?'var(--accent)':'var(--text)',display:'flex',flexDirection:'column',gap:'3px'}} onClick={()=>loadStrategy(s.id)}>
                  <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
                    <span style={{fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1,minWidth:0}}>{s.name}</span>
                    {s.is_owner&&user?.is_admin&&(
                      <button
                        onClick={e=>toggleStrategyAlerts(s,e)}
                        title={s.signal_alerts_enabled?'Signal alerts enabled — click to disable':'Enable Discord signal alerts'}
                        style={{padding:'2px 6px',fontSize:'8px',fontWeight:700,fontFamily:"'JetBrains Mono',monospace",borderRadius:'9px',cursor:'pointer',border:`1px solid ${s.signal_alerts_enabled?'var(--success)':'var(--border)'}`,background:s.signal_alerts_enabled?'var(--success)':'transparent',color:s.signal_alerts_enabled?'var(--on-accent)':'var(--text-muted)',flexShrink:0}}
                      >ALERT {s.signal_alerts_enabled?'ON':'OFF'}</button>
                    )}
                  </div>
                  <span style={{fontSize:'10px',color:'var(--text-muted)',fontFamily:"'JetBrains Mono',monospace"}}>{s.updated_at?new Date(s.updated_at).toLocaleDateString():''}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Right panel */}
        <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',minHeight:0}}>
          {/* Tab bar */}
          <div style={{background:'var(--surface)',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',padding:'0 10px',gap:'2px',flexShrink:0,height:'40px',overflowX:'auto'}}>
            {[['regime','Regime'],['signals','Signals'],['settings','Settings'],['backtest','Backtest'],['chart','Signal Chart']].map(([k,l])=>(
              <button key={k} style={{padding:'4px 12px',fontSize:'11px',fontWeight:tab===k?700:500,fontFamily:"'JetBrains Mono',monospace",borderRadius:'4px 4px 0 0',cursor:'pointer',background:tab===k?'var(--bg)':'transparent',color:tab===k?'var(--accent)':'var(--text-muted)',border:tab===k?'1px solid var(--border)':'1px solid transparent',borderBottom:tab===k?'1px solid var(--bg)':'none',whiteSpace:'nowrap'}} onClick={()=>setTab(k)}>
                {l}{errorTabs.has(k)&&<span title="This tab has fields that need fixing" style={{marginLeft:'5px',color:'var(--warning)',fontSize:'12px'}}>⚠</span>}
              </button>
            ))}
            {saveMsg&&<span style={{fontSize:'11px',color:'var(--success)',fontFamily:"'JetBrains Mono',monospace",whiteSpace:'nowrap'}}>{saveMsg}</span>}
            {saveErr&&<span style={{fontSize:'11px',color:'var(--error)',fontFamily:"'JetBrains Mono',monospace",whiteSpace:'nowrap'}}>{saveErr}</span>}
            <button style={btnG} onClick={saveStrategy} disabled={saving}>{saving?'…':'Save'}</button>
            {activeId&&<button style={btnD} onClick={deleteStrategy}>Del</button>}
          </div>

          {/* Editor */}
          {tab!=='backtest'&&tab!=='chart'&&(
            <div style={{flex:1,overflowY:'auto',padding:isMobile?'10px':'14px'}}>
              {/* Name/desc */}
              <div style={sec}>
                <div style={sBody}>
                  <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 2fr',gap:'10px'}}>
                    <div><label style={lbl}>Name</label><input style={inpW} value={name} onChange={e=>{setName(e.target.value);setSaveMsg('');setSaveErr('');}} placeholder="Strategy name" maxLength={100}/></div>
                    <div><label style={lbl}>Description</label><input style={inpW} value={description} onChange={e=>setDescription(e.target.value)} placeholder="Optional"/></div>
                  </div>
                </div>
              </div>

              {/* Settings */}
              {tab==='settings'&&(
                <div style={sec}>
                  <div style={sHdr}><span style={sTtl}>Settings</span></div>
                  <div style={sBody}>
                    <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr 1fr':'repeat(3,1fr)',gap:'12px'}}>
                      <div>
                        <label style={lbl}>Backtest Execution</label>
                        <select style={sel} value={sJson.backtest_timeframe||'1d'} onChange={e=>setSJson(p=>({...p,backtest_timeframe:e.target.value}))}>
                          <option value="1d">Daily (existing)</option>
                          <option value="1m">1 minute (intraday)</option>
                        </select>
                        <div style={{fontSize:'10px',color:'var(--text-muted)',marginTop:'2px'}}>Minute execution uses next available traded minute open. Indicators may use any timeframe.</div>
                      </div>
                      <div>
                        <label style={lbl}>Base Currency</label>
                        <select style={sel} value={sJson.base_currency} onChange={e=>setSJson(p=>({...p,base_currency:e.target.value}))}>
                          <option value="AUD">AUD</option>
                          <option value="USD">USD</option>
                        </select>
                      </div>
                      <div>
                        <label style={lbl}>Starting Regime</label>
                        <select style={sel} value={sJson.starting_regime_id||''} onChange={e=>setSJson(p=>({...p,starting_regime_id:e.target.value}))}>
                          {sJson.regime_levels.map(l=><option key={l.id} value={l.id}>{l.level} · {l.label}</option>)}
                        </select>
                        <div style={{fontSize:'10px',color:'var(--text-muted)',marginTop:'2px'}}>Used until the first signal targets another regime.</div>
                      </div>
                      <div>
                        <label style={lbl}>Rebalance Every</label>
                        <div style={{display:'flex',gap:'6px',alignItems:'center'}}>
                          <input type="checkbox" checked={!!sJson.rebalance_every}
                            onChange={e=>setSJson(p=>({...p,rebalance_every:e.target.checked?{n:6,unit:'months'}:null}))}/>
                          {sJson.rebalance_every&&<>
                            <input style={{...inp,width:'50px'}} type="number" min="1"
                              value={sJson.rebalance_every.n}
                              onChange={e=>setSJson(p=>({...p,rebalance_every:{...p.rebalance_every,n:parseInt(e.target.value)||1}}))}/>
                            <select style={sel} value={sJson.rebalance_every.unit}
                              onChange={e=>setSJson(p=>({...p,rebalance_every:{...p.rebalance_every,unit:e.target.value}}))}>
                              {REBALANCE_UNITS.map(u=><option key={u.value} value={u.value}>{u.label}</option>)}
                            </select>
                          </>}
                        </div>
                        <div style={{fontSize:'10px',color:'var(--text-muted)',marginTop:'2px'}}>Periodic rebalance to the active level's allocations. Off = only on level/signal change.</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Regime levels */}
              {tab==='regime'&&(
                <div style={sec}>
                  <div style={{...sHdr,justifyContent:'flex-start',gap:'12px',flexWrap:'wrap'}}>
                    <span style={sTtl}>Portfolio Regimes</span>
                    <LibDropdown label="Load saved" items={savedRegimes} open={libOpen==='regimes'} onToggle={()=>setLibOpen(o=>o==='regimes'?null:'regimes')} onPick={loadRegimeFromLib} onDelete={(id,nm)=>deleteLibItem('regimes',id,nm)}/>
                    <span style={{fontSize:'10px',color:'var(--text-muted)'}}>Each regime is a portfolio position · drag ⠿ to reorder</span>
                  </div>
                  <div style={sBody}>
                    {sJson.regime_levels.map((lvl,idx)=>(
                      <div
                        key={lvl.id}
                        style={{...lvlRow(openLevels.has(lvl.id)), opacity:dragIdx===idx?0.4:(lvl.enabled===false?0.55:1),...(hasErr(`lvl:${lvl.id}`)?{borderColor:'var(--error)',borderWidth:'1.5px'}:{})}}
                        onDragOver={e=>{e.preventDefault();}}
                        onDrop={e=>{e.preventDefault();if(dragIdx!==null&&dragIdx!==idx){moveLevelItem(dragIdx,idx);}setDragIdx(null);}}
                      >
                        <div style={lvlHdr} onClick={()=>togLvl(lvl.id)}>
                          <span
                            draggable
                            onDragStart={e=>{setDragIdx(idx);e.stopPropagation();}}
                            onDragEnd={()=>setDragIdx(null)}
                            onClick={e=>e.stopPropagation()}
                            title="Drag to reorder"
                            style={{cursor:'grab',color:'var(--text-muted)',fontSize:'14px',flexShrink:0,padding:'0 2px',userSelect:'none'}}
                          >⠿</span>
                          <span style={{fontSize:'11px',fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:'var(--accent)',width:'18px',flexShrink:0,textAlign:'center'}}>{lvl.level}</span>
                          <input style={{...inp,width:'200px',flexShrink:0,fontSize:'12px',...(hasErr(`lvlname:${lvl.id}`)?{background:'var(--error-dim)',borderColor:'var(--error)'}:{})}} value={lvl.label} onClick={e=>e.stopPropagation()} onChange={e=>updLevel(idx,l=>({...l,label:e.target.value}))} placeholder={`Regime ${lvl.level}`}/>
                          <button
                            onClick={e=>{e.stopPropagation();updLevel(idx,l=>({...l,enabled:l.enabled===false?true:false}));}}
                            title={lvl.enabled===false?'Blank: if this regime is active, the portfolio holds its current positions. Click to enable.':'Enabled: rebalances to allocations. Click to make blank.'}
                            style={{marginLeft:'4px',padding:'2px 8px',fontSize:'10px',fontWeight:700,fontFamily:"'JetBrains Mono',monospace",borderRadius:'10px',cursor:'pointer',border:'1px solid '+(lvl.enabled===false?'var(--border)':'var(--success)'),background:lvl.enabled===false?'var(--bg)':'var(--success)',color:lvl.enabled===false?'var(--text-muted)':'var(--on-accent)',flexShrink:0}}
                          >{lvl.enabled===false?'BLANK':'ON'}</button>
                          <span style={{fontSize:'10px',color:'var(--text-muted)',marginLeft:'6px',flexShrink:0}}>{lvl.allocations.length}a</span>
                          <button
                            onClick={e=>{e.stopPropagation();saveRegimeToLib(lvl);}}
                            title="Save this regime level to your library (uses its label as the name)"
                            style={{marginLeft:'4px',padding:'2px 8px',fontSize:'10px',fontWeight:700,fontFamily:"'JetBrains Mono',monospace",borderRadius:'10px',cursor:'pointer',border:'1px solid var(--accent)',background:'transparent',color:'var(--accent)',flexShrink:0,...(hasErr(`lvlname:${lvl.id}`)?{borderColor:'var(--error)',color:'var(--error)'}:{})}}
                          >💾</button>
                          <button style={{...sRemBtn,marginLeft:'4px'}} onClick={e=>{e.stopPropagation();if(window.confirm(`Delete ${lvl.label}?`))remLevelItem(lvl.id);}} title="Delete level">✕</button>
                          <span style={{fontSize:'11px',color:'var(--text-muted)',marginLeft:'4px',flexShrink:0}}>{openLevels.has(lvl.id)?'▲':'▼'}</span>
                          <div style={{flex:1}}/>
                        </div>
                        {openLevels.has(lvl.id)&&(
                          <div style={lp}>
                            <div>
                              <div style={{display:'flex',gap:'6px',alignItems:'center',marginBottom:'6px'}}>
                                <strong style={{fontSize:'11px',color:'var(--text)',fontFamily:"'JetBrains Mono',monospace"}}>ALLOCATIONS (sum to 100%)</strong>
                                <button style={addB} onClick={()=>addAlloc(idx)}>+ Add</button>
                              </div>
                              {lvl.allocations.map(a=><AllocationRow key={a.id} alloc={a} lists={lists} error={hasErr(`alloc:${a.id}`)} onChange={patch=>updAlloc(idx,a.id,patch)} onRemove={()=>remAlloc(idx,a.id)}/>)}
                              <div style={{fontSize:'10px',color:'var(--text-muted)',marginTop:'2px'}}>Sum: {lvl.allocations.reduce((s,a)=>s+(Number(a.pct)||0),0)}%</div>
                            </div>
                            <div>
                              <label style={{fontSize:'10px',fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',fontFamily:"'JetBrains Mono',monospace",display:'block',marginBottom:'4px'}}>DCA Target (while in this regime)</label>
                              <DcaTargetRow value={lvl.dca_ticker} onChange={v=>updLevel(idx,l=>({...l,dca_ticker:v}))}/>
                              <div style={{fontSize:'10px',color:'var(--text-muted)',marginTop:'3px'}}>DCA contributions on anniversary. "Cash" = hold as uninvested balance.</div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    <button style={{...addB,marginTop:'6px'}} onClick={addLevelItem}>+ Add Level</button>
                  </div>
                </div>
              )}

              {/* Signals */}
              {tab==='signals'&&(
                <div style={sec}>
                  <div style={{...sHdr,justifyContent:'flex-start',gap:'10px',flexWrap:'wrap'}}>
                    <span style={sTtl}>Signals</span>
                    <button style={addB} onClick={addSig}>+ Add</button>
                    <LibDropdown label="Load saved" items={savedSignals} open={libOpen==='signals'} onToggle={()=>setLibOpen(o=>o==='signals'?null:'signals')} onPick={loadSignalFromLib} onDelete={(id,nm)=>deleteLibItem('signals',id,nm)}/>
                  </div>
                  <div style={sBody}>
                    {sJson.signals.length===0&&<div style={{fontSize:'12px',color:'var(--text-muted)'}}>No signals. The strategy stays in its starting regime until a signal fires.</div>}
                    {sJson.signals.map(sig=>(
                      <div key={sig.id} style={{...lvlRow(openSignals.has(sig.id)),marginBottom:'8px',...(hasErr(`sig:${sig.id}`)?{borderColor:'var(--error)',borderWidth:'1.5px'}:{})}}>
                        <div style={lvlHdr} onClick={()=>togSig(sig.id)}>
                          <span style={targetB}>→ {regimeForId(sig.target_regime_id)?`R${regimeForId(sig.target_regime_id).level}`:'SET TARGET'}</span>
                          <input style={{...inp,width:'200px',flexShrink:0,margin:'0 6px',fontSize:'12px',...(hasErr(`signame:${sig.id}`)?{background:'var(--error-dim)',borderColor:'var(--error)'}:{})}} value={sig.label} onClick={e=>e.stopPropagation()} onChange={e=>updSig(sig.id,'label',e.target.value)} placeholder="Signal label"/>
                          <input type="color" aria-label="Signal marker colour" title="Signal marker colour" value={signalColor(sig,0)} onClick={e=>e.stopPropagation()} onChange={e=>updSig(sig.id,'color',e.target.value)} style={{width:'30px',height:'26px',padding:0,border:'1px solid var(--border)',borderRadius:'4px',background:'transparent',cursor:'pointer',flexShrink:0}}/>
                          <span style={{fontSize:'11px',color:'var(--text-muted)',marginLeft:'6px'}}>{openSignals.has(sig.id)?'▲':'▼'}</span>
                          <button
                            onClick={e=>{e.stopPropagation();saveSignalToLib(sig);}}
                            title="Save this signal to your library (uses its label as the name)"
                            style={{marginLeft:'6px',padding:'2px 8px',fontSize:'10px',fontWeight:700,fontFamily:"'JetBrains Mono',monospace",borderRadius:'10px',cursor:'pointer',border:'1px solid var(--accent)',background:'transparent',color:'var(--accent)',flexShrink:0,...(hasErr(`signame:${sig.id}`)?{borderColor:'var(--error)',color:'var(--error)'}:{})}}
                          >💾</button>
                          <button style={{...sRemBtn,marginLeft:'6px'}} onClick={e=>{e.stopPropagation();remSig(sig.id);}}>✕</button>
                          <div style={{flex:1}}/>
                        </div>
                        {openSignals.has(sig.id)&&(
                          <div style={lp}>
                            <div style={{display:'flex',gap:'12px',flexWrap:'wrap'}}>
                              <div><label style={lbl}>Type</label><select style={sel} value={sig.type||'conditions'} onChange={e=>updSig(sig.id,'type',e.target.value)}><option value="conditions">Conditions</option><option value="smc">SMC structure</option></select></div>
                              {sig.type==='smc'&&<div><label style={lbl}>Structure Direction</label><select style={sel} value={sig.direction} onChange={e=>updSig(sig.id,'direction',e.target.value)}><option value="bullish">Bullish break</option><option value="bearish">Bearish break</option></select></div>}
                              <div><label style={lbl}>Go To Regime</label><select style={{...sel,...(hasErr(`sig:${sig.id}`)&&!regimeForId(sig.target_regime_id)?{borderColor:'var(--error)'}:{})}} value={sig.target_regime_id||''} onChange={e=>updSig(sig.id,'target_regime_id',e.target.value)}>
                                <option value="">— choose —</option>
                                {sJson.regime_levels.map(l=><option key={l.id} value={l.id}>{l.level} · {l.label}</option>)}
                              </select></div>
                            </div>
                            {sig.type==='smc'?(
                              <div>
                                <div style={{marginBottom:'6px'}}>
                                  <strong style={{fontSize:'11px',color:'var(--text)',fontFamily:"'JetBrains Mono',monospace"}}>SMC STRUCTURE (fires on the day a close-confirmed break prints)</strong>
                                </div>
                                <div style={{display:'flex',gap:'12px',alignItems:'flex-end',flexWrap:'wrap'}}>
                                  <div><label style={lbl}>Ticker</label><TickerSearch value={sig.smc.ticker} onChange={v=>updSig(sig.id,'smc',{...sig.smc,ticker:v})} placeholder="Ticker…" width="140px" error={hasErr(`smct:${sig.id}`)}/></div>
                                  <div><label style={lbl}>Trigger</label><select style={sel} value={sig.smc.trigger} onChange={e=>updSig(sig.id,'smc',{...sig.smc,trigger:e.target.value})}>
                                    <option value="choch">ChoCh (reversal)</option>
                                    <option value="bos">BoS (continuation)</option>
                                    <option value="structure">BoS or ChoCh</option>
                                  </select></div>
                                  <div><label style={lbl} title="Break candle body must be at least this × ATR14 to qualify. 0 = filter off.">Displacement ×ATR</label><input style={{...inp,width:'60px'}} type="number" min="0" step="0.1" value={sig.smc.dispAtr} onChange={e=>updSig(sig.id,'smc',{...sig.smc,dispAtr:e.target.value})}/></div>
                                </div>
                              </div>
                            ):(
                              <div>
                                <div style={{display:'flex',gap:'6px',alignItems:'center',marginBottom:'6px'}}>
                                  <strong style={{fontSize:'11px',color:'var(--text)',fontFamily:"'JetBrains Mono',monospace"}}>CONDITIONS (fires on transition only)</strong>
                                  <button style={addB} onClick={()=>addSC(sig.id)}>+ Add</button>
                                </div>
                                {sig.conditions.map(c=><ConditionRow key={c.id} cond={c} allowTimeframe={sJson.backtest_timeframe==='1m'} error={hasErr(`cond:${c.id}`)} onChange={nc=>updSC(sig.id,c.id,nc)} onRemove={()=>remSC(sig.id,c.id)}/>)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Signal chart tab: intraday candles with signal firings */}
          {tab==='chart'&&(
            <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',minHeight:0,padding:isMobile?'8px':'12px 14px'}}>
              <SignalChart activeId={activeId} sJson={sJson} saveStrategy={saveStrategy} styles={{sec,sBody,lbl,inp,sel,btn,btnG}}
                trades={btResults?.result_json?.engine_version==='intraday-1m-v1'?btResults.result_json.trades:null}
                tradesAt={btResults?.computed_at} jump={chartJump} onJumpDone={()=>setChartJump(null)}/>
            </div>
          )}

          {/* Backtest tab */}
          {tab==='backtest'&&(
            <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',minHeight:0,padding:isMobile?'8px':'12px 14px'}}>
              <div style={{...sec,marginBottom:'10px',flexShrink:0}}>
                <div style={sBody}>
                  <div style={{display:'flex',gap:'10px',alignItems:'flex-end',flexWrap:'wrap'}}>
                    <div><label style={lbl}>Initial Cash ({sJson.base_currency})</label><input style={{...inp,width:'100px'}} type="number" min="1" value={btCash} onChange={e=>setBtCash(e.target.value)}/></div>
                    <div>
                      <label style={{...lbl,display:'flex',alignItems:'center',gap:'5px',cursor:'pointer'}} title="Untick to turn DCA off (sets the amount to 0; the amount is remembered for re-ticking). Applies on Run Backtest, which saves first.">
                        <input type="checkbox" checked={Number(sJson.dca_amount)>0}
                          onChange={e=>setSJson(p=>({...p,dca_amount:e.target.checked?(lastDca.current||100):0}))}/> DCA
                      </label>
                      <input style={{...inp,width:'100px'}} type="number" min="0" value={sJson.dca_amount} disabled={!(Number(sJson.dca_amount)>0)}
                        onChange={e=>setSJson(p=>({...p,dca_amount:e.target.value}))} placeholder="0 = off"/>
                    </div>
                    <div>
                      <label style={lbl}>DCA Frequency</label>
                      <div style={{display:'flex',gap:'6px'}}>
                        <input style={{...inp,width:'50px'}} type="number" min="1"
                          value={sJson.dca_frequency.n}
                          onChange={e=>setSJson(p=>({...p,dca_frequency:{...p.dca_frequency,n:parseInt(e.target.value)||1}}))}/>
                        <select style={sel} value={sJson.dca_frequency.unit}
                          onChange={e=>setSJson(p=>({...p,dca_frequency:{...p.dca_frequency,unit:e.target.value}}))}>
                          <option value="days">days</option>
                          <option value="weeks">weeks</option>
                          <option value="months">months</option>
                        </select>
                      </div>
                    </div>
                    <button style={btn} onClick={triggerBacktest} disabled={btRunning||saving||!activeId} title="Saves the strategy first, then runs — the engine reads the saved settings">{saving?'Saving…':btRunning?(btStatus==='running'?'Running…':'Queued…'):'Run Backtest'}</button>
                    {!activeId&&<span style={{fontSize:'11px',color:'var(--text-muted)'}}>Save first.</span>}
                    <span style={{fontSize:'11px',color:'var(--text-muted)'}}>Starts from earliest date all tickers have data. Run Backtest saves the strategy first — DCA and settings changes apply to the run you start.</span>
                  </div>
                </div>
              </div>

              {!btResults&&!btRunning&&<div style={{display:'flex',alignItems:'center',justifyContent:'center',flex:1,color:'var(--text-muted)',fontSize:'13px',flexDirection:'column',gap:'10px'}}><span style={{fontSize:'28px'}}>📈</span><span>Run a backtest to see results.</span></div>}
              {btRunning&&!btResults&&<div style={{display:'flex',alignItems:'center',justifyContent:'center',flex:1,color:'var(--text-muted)',fontSize:'13px'}}>⏳ Running…</div>}

              {btResults&&(
                <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius)',display:'flex',flexDirection:'column',flex:1,minHeight:0,overflow:'hidden'}}>
                  {/* Controls */}
                  <div style={{padding:'8px 12px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:'6px',flexWrap:'wrap',flexShrink:0}}>
                    <span style={{fontSize:'11px',color:'var(--text-muted)',fontFamily:"'JetBrains Mono',monospace"}}>Window</span>
                    <input style={{...sIn('46px')}} type="number" min="0" value={winY} onChange={e=>setWindowYM(e.target.value,winM)} title="Window length: years"/>
                    <span style={{fontSize:'11px',color:'var(--text-muted)',fontFamily:"'JetBrains Mono',monospace"}}>y</span>
                    <input style={{...sIn('46px')}} type="number" min="0" max="11" value={winM} onChange={e=>setWindowYM(winY,e.target.value)} title="Window length: months"/>
                    <span style={{fontSize:'11px',color:'var(--text-muted)',fontFamily:"'JetBrains Mono',monospace"}}>m</span>
                    {/* Open the hovered day in the Signal Chart tab (1m backtests only) */}
                    {btResults.result_json?.engine_version==='intraday-1m-v1'&&lastDay&&(
                      <button
                        style={{padding:'3px 9px',fontSize:'11px',fontWeight:600,border:'1px solid var(--accent)',borderRadius:'4px',cursor:'pointer',fontFamily:"'JetBrains Mono',monospace",background:'transparent',color:'var(--accent)'}}
                        onClick={()=>{setChartJump({date:lastDay});setTab('chart');}}
                        title="Open the last day you hovered (or tapped) in the Signal Chart tab, with its fills"
                      >📊 Chart {lastDay}</button>
                    )}
                    {/* Marker toggles */}
                    <button
                      style={{padding:'3px 9px',fontSize:'11px',fontWeight:600,border:'1px solid var(--border)',borderRadius:'4px',cursor:'pointer',fontFamily:"'JetBrains Mono',monospace",background:showRegimeMarkers?'var(--accent)':'var(--bg)',color:showRegimeMarkers?'var(--on-accent)':'var(--text-muted)',marginLeft:'8px'}}
                      onClick={()=>setShowRegimeMarkers(v=>!v)}
                      title="Show/hide regime-change markers (circles)"
                    >◯ Regime</button>
                    <button
                      style={{padding:'3px 9px',fontSize:'11px',fontWeight:600,border:'1px solid var(--border)',borderRadius:'4px',cursor:'pointer',fontFamily:"'JetBrains Mono',monospace",background:showSignalMarkers?'var(--accent)':'var(--bg)',color:showSignalMarkers?'var(--on-accent)':'var(--text-muted)'}}
                      onClick={()=>setShowSignalMarkers(v=>!v)}
                      title="Show/hide signal markers (solid = regime switch, hollow = no switch)"
                    >△ Signals</button>
                    <label title="Off: regime-changing signals only. On: every signal fire, with hollow triangles for unchanged regimes." style={{display:'flex',alignItems:'center',gap:'4px',fontSize:'11px',color:'var(--text)',cursor:showSignalMarkers?'pointer':'default',fontFamily:"'JetBrains Mono',monospace"}}>
                      <input type="checkbox" checked={showAllSignalFires} disabled={!showSignalMarkers} onChange={e=>setShowAllSignalFires(e.target.checked)}/> Every fire
                    </label>
                    <button
                      style={{padding:'3px 9px',fontSize:'11px',fontWeight:600,border:'1px solid var(--border)',borderRadius:'4px',cursor:'pointer',fontFamily:"'JetBrains Mono',monospace",background:showPortfolioData?'var(--accent)':'var(--bg)',color:showPortfolioData?'var(--on-accent)':'var(--text-muted)'}}
                      onClick={()=>setShowPortfolioData(v=>!v)}
                      title="Show/hide the pinned portfolio data overlay (top-left)"
                    >▦ Portfolio_data</button>
                    <button
                      style={{padding:'3px 9px',fontSize:'11px',fontWeight:600,border:'1px solid var(--border)',borderRadius:'4px',cursor:'pointer',fontFamily:"'JetBrains Mono',monospace",background:showSignalLabels?'var(--accent)':'var(--bg)',color:showSignalLabels?'var(--on-accent)':'var(--text-muted)'}}
                      onClick={()=>setShowSignalLabels(v=>!v)}
                      title="Show/hide signal name labels on hover"
                    >△ Signal_labels</button>
                    <div ref={cmpWrap} style={{position:'relative'}}>
                      <input style={{...inp,width:isMobile?'110px':'140px'}} placeholder="+ Compare" value={cmpQ} onChange={e=>handleCmpSearch(e.target.value)} onFocus={()=>cmpQ&&setCmpOpen(true)}/>
                      {cmpOpen&&cmpRes.length>0&&(
                        <div style={sDrop}>
                          {cmpRes.slice(0,8).map(r=>(
                            <div key={`${r.symbol}:${r.exchange}`} style={sDI} onMouseDown={()=>{addCmpTicker(r.symbol,r.exchange,r.name);setCmpQ('');setCmpOpen(false);}}>
                              <span style={sDSym}>{r.symbol}:{r.exchange}</span><span style={sDName}>{r.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Pills */}
                  {cmpTickers.length>0&&(
                    <div style={{padding:'6px 12px',borderBottom:'1px solid var(--border)',display:'flex',gap:'6px',flexWrap:'wrap',flexShrink:0}}>
                      <span style={pill('var(--accent)')}><span style={{width:'8px',height:'8px',borderRadius:'50%',background:'var(--accent)',display:'inline-block'}}/><span style={{color:'var(--accent)'}}>{name||'Strategy'}</span></span>
                      {cmpTickers.map(t=>(
                        <span key={t.key} style={pill(t.color)}>
                          <input type="color" aria-label={`Colour for ${t.key}`} title={`Change ${t.key} line colour`} value={t.color} onChange={e=>setCmpTickers(prev=>prev.map(item=>item.key===t.key?{...item,color:e.target.value}:item))} style={{width:'22px',height:'22px',padding:0,border:0,background:'transparent',cursor:'pointer',flexShrink:0}}/>
                          <span style={{color:t.color}}>{t.key}</span>
                          <button style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-muted)',fontSize:'13px',padding:'0 2px',lineHeight:1}} onClick={()=>removeCmpTicker(t.key)}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Chart + right-hand stats panel */}
                  <div style={{flex:1,display:'flex',minHeight:0}}>
                  <div style={{flex:1,position:'relative',minWidth:0}} onMouseLeave={()=>setHoverData(null)}>
                    <canvas ref={canvasRef}/>
                    {/* Pinned portfolio-data overlay (top-left). Follows cursor along x-axis. */}
                    {showPortfolioData&&hoverData&&(
                      <div style={{position:'absolute',top:'8px',left:'8px',background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'7px 9px',fontSize:'10px',fontFamily:"'JetBrains Mono',monospace",color:'var(--text)',pointerEvents:'none',maxWidth:'230px',boxShadow:'0 2px 8px rgba(0,0,0,0.12)',lineHeight:1.5}}>
                        <div style={{fontWeight:700,marginBottom:'2px'}}>{new Date(hoverData.x).toLocaleDateString('en-AU',{day:'2-digit',month:'short',year:'numeric'})}</div>
                        <div>Active regime: <strong>{hoverData.level}</strong></div>
                        <div>Total: <strong>${Number(hoverData.pv).toLocaleString('en-AU',{minimumFractionDigits:2,maximumFractionDigits:2})}</strong></div>
                        <div>Cash: ${Number(hoverData.cash).toLocaleString('en-AU',{maximumFractionDigits:0})} ({((hoverData.cash/(hoverData.pv||1))*100).toFixed(1)}%)</div>
                        {Object.keys(hoverData.holdings||{}).length>0&&(
                          <div style={{marginTop:'2px',borderTop:'1px solid var(--border)',paddingTop:'2px'}}>
                            {Object.keys(hoverData.holdings).map(t=>{
                              const h=hoverData.holdings[t];const val=h.value||0;const pct=(val/(hoverData.pv||1))*100;
                              return <div key={t}>{t}: {Number(h.shares).toFixed(2)} · ${Number(val).toLocaleString('en-AU',{maximumFractionDigits:0})} ({pct.toFixed(1)}%)</div>;
                            })}
                          </div>
                        )}
                      </div>
                    )}
                    {/* Mobile stats slide-out: toggle button + overlay panel */}
                    {isMobile&&stats&&(
                      <button onClick={()=>setStatsOpen(o=>!o)} style={{position:'absolute',top:'8px',right:'8px',zIndex:6,padding:'3px 9px',fontSize:'11px',fontWeight:600,border:'1px solid var(--border)',borderRadius:'4px',cursor:'pointer',fontFamily:"'JetBrains Mono',monospace",background:statsOpen?'var(--accent)':'var(--bg)',color:statsOpen?'var(--on-accent)':'var(--text-muted)'}}>{statsOpen?'✕':'Stats'}</button>
                    )}
                    {isMobile&&statsOpen&&stats&&(
                      <div style={{position:'absolute',top:0,right:0,bottom:0,width:'200px',zIndex:5,background:'var(--surface)',borderLeft:'1px solid var(--border)',padding:'38px 10px 10px',overflowY:'auto',display:'flex',flexDirection:'column',gap:'14px'}}>{statsContent}</div>
                    )}
                  </div>
                  {!isMobile&&stats&&(
                    <div style={{width:'200px',flexShrink:0,borderLeft:'1px solid var(--border)',padding:'10px 12px',overflow:'auto',display:'flex',flexDirection:'column',gap:'14px'}}>{statsContent}</div>
                  )}
                  </div>
                  {/* Holdings + list rankings panel: follows chart hover, falls back to
                      final day. Ranks the strategy selects are bold, rest of family greyed. */}
                  {btResults?.result_json?.rank_lists&&Object.keys(btResults.result_json.rank_lists).length>0&&(()=>{
                    const meta=btResults.result_json.rank_lists;
                    const ser=btResults.result_json.series||[];
                    const last=ser[ser.length-1];
                    if(!last)return null;
                    const day=hoverData
                      ?{d:new Date(hoverData.x).toISOString().slice(0,10),pv:hoverData.pv,holdings:hoverData.holdings||{},ranks:hoverData.rankings}
                      :{d:last.d,pv:last.portfolio_value,holdings:last.holdings||{},ranks:last.rankings};
                    const fmtScore=(info,s)=>s==null?'—':(info.mode==='metric'&&info.metric_type==='return')?(s*100).toFixed(1)+'%':Number(s).toFixed(2);
                    return (
                      <div style={{display:'flex',gap:'16px',flexWrap:'wrap',padding:'6px 8px',border:'1px solid var(--border)',borderRadius:'var(--radius)',margin:'6px 0',fontSize:'10px',maxHeight:'110px',overflowY:'auto',flexShrink:0}}>
                        <div>
                          <div style={{color:'var(--text-muted)',marginBottom:'2px'}}>Holdings · {day.d}{hoverData?'':' (latest)'}</div>
                          {Object.keys(day.holdings).length===0&&<div>100% CASH</div>}
                          {Object.entries(day.holdings).map(([t,h])=>(
                            <div key={t}>{t} {((h.value||0)/(day.pv||1)*100).toFixed(1)}%</div>
                          ))}
                        </div>
                        {Object.entries(meta).map(([lid,info])=>(
                          <div key={lid}>
                            <div style={{color:'var(--text-muted)',marginBottom:'2px'}}>{info.name}</div>
                            {((day.ranks||{})[lid]||[]).map(r=>{
                              const sel=r.score!=null&&(info.selected_ranks||[]).includes(r.rank);
                              return <div key={r.ticker} style={sel?{fontWeight:700}:{color:'var(--text-muted)'}}>{r.rank}. {r.ticker} {fmtScore(info,r.score)}</div>;
                            })}
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Range selector: the band is the window, drawn proportionally across
                      the full timeline. Drag the band to move it, drag an edge to resize.
                      During a drag only dragWin updates (band/captions/date inputs live);
                      on release it commits to winStart + winY/winM, which rebuilds the
                      chart — the same state the y/m inputs, wheel zoom and chart pan
                      write, so every control stays in sync. */}
                  {(()=>{
                    const b=boundsRef.current; if(b.min==null)return null;
                    const width=dragWin?dragWin.width:ymToMs(winY,winM);
                    const maxStart=Math.max(b.min,b.max-width);
                    const ws=Math.min(Math.max(dragWin?dragWin.start:(winStart??b.min),b.min),maxStart);
                    const we=Math.min(ws+width,b.max);
                    const total=Math.max(1,b.max-b.min);
                    const L=((ws-b.min)/total)*100, R=((we-b.min)/total)*100;
                    const iso=t=>new Date(t).toISOString().slice(0,10);
                    const clampStart=(s,w)=>Math.min(Math.max(s,b.min),Math.max(b.min,b.max-w));
                    const startDrag=(e,mode)=>{
                      e.preventDefault();
                      const rect=e.currentTarget.parentElement.getBoundingClientRect();
                      const x0=e.clientX, ws0=ws, w0=width;
                      const move=ev=>{
                        const dx=(ev.clientX-x0)/Math.max(1,rect.width)*total;
                        let s,w;
                        if(mode==='m'){s=clampStart(ws0+dx,w0);w=w0;}
                        else{
                          // Edge drag: clamp to the series, then snap the width to whole
                          // months (the y/m inputs' granularity) so release commits cleanly.
                          const rs=mode==='l'?Math.max(b.min,Math.min(ws0+dx,ws0+w0)):ws0;
                          const re=mode==='r'?Math.min(b.max,Math.max(ws0+w0+dx,ws0)):ws0+w0;
                          let{y,m}=msToYM(re-rs);if(y===0&&m===0)m=1;
                          w=Math.min(ymToMs(y,m),total);
                          s=clampStart(mode==='l'?re-w:rs,w);
                        }
                        dragRef.current={start:s,width:w};
                        setDragWin(dragRef.current);
                      };
                      const up=()=>{
                        window.removeEventListener('pointermove',move);
                        window.removeEventListener('pointerup',up);
                        const dw=dragRef.current;dragRef.current=null;setDragWin(null);
                        if(dw){const{y,m}=msToYM(dw.width);setWindowYM(y,m);setWinStart(dw.start);}
                      };
                      window.addEventListener('pointermove',move);
                      window.addEventListener('pointerup',up);
                    };
                    const setFrom=v=>{const t=Date.parse(v+'T00:00:00Z');if(Number.isFinite(t))setWinStart(clampStart(t,width));};
                    const setTo=v=>{
                      const t=Date.parse(v+'T00:00:00Z');if(!Number.isFinite(t))return;
                      let{y,m}=msToYM(Math.min(t,b.max)-ws);if(y===0&&m===0)m=1;
                      setWindowYM(y,m);setWinStart(clampStart(ws,ymToMs(y,m)));
                    };
                    const edge=(mode,left)=><div key={mode} onPointerDown={e=>startDrag(e,mode)} title="Drag to resize the window" style={{position:'absolute',top:'-3px',bottom:'-3px',left:`calc(${left}% - 4px)`,width:'8px',cursor:'ew-resize',touchAction:'none'}}/>;
                    return (
                      <div style={{padding:'6px 12px',borderTop:'1px solid var(--border)',flexShrink:0}}>
                        <div style={{position:'relative',height:'14px',background:'var(--bg)',border:'1px solid var(--border)',borderRadius:'4px',userSelect:'none'}}>
                          <div onPointerDown={e=>startDrag(e,'m')} title="Drag to move the window" style={{position:'absolute',top:0,bottom:0,left:L+'%',width:Math.max(0,R-L)+'%',background:'var(--accent-glass)',borderLeft:'2px solid var(--accent)',borderRight:'2px solid var(--accent)',borderRadius:'2px',cursor:'grab',touchAction:'none'}}/>
                          {edge('l',L)}
                          {edge('r',R)}
                        </div>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:'8px',flexWrap:'wrap',marginTop:'4px'}}>
                          <span style={{fontSize:'10px',color:'var(--text-muted)',fontFamily:"'JetBrains Mono',monospace",whiteSpace:'nowrap'}}>{new Date(ws).toLocaleDateString('en-AU',{month:'short',year:'numeric'})} – {new Date(we).toLocaleDateString('en-AU',{month:'short',year:'numeric'})}</span>
                          <span style={{display:'flex',gap:'6px',alignItems:'center',fontSize:'10px',color:'var(--text-muted)',fontFamily:"'JetBrains Mono',monospace"}}>
                            From <input type="date" style={{...inp,padding:'2px 4px',fontSize:'11px'}} value={iso(ws)} min={iso(b.min)} max={iso(b.max)} onChange={e=>setFrom(e.target.value)}/>
                            To <input type="date" style={{...inp,padding:'2px 4px',fontSize:'11px'}} value={iso(we)} min={iso(b.min)} max={iso(b.max)} onChange={e=>setTo(e.target.value)}/>
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
